import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  TruckIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  PauseIcon,
  CheckIcon,
  CameraIcon,
  ChatBubbleLeftIcon,
  WrenchScrewdriverIcon,
  PlusIcon,
  ArrowPathIcon,
  TrashIcon,
  XMarkIcon,
  PhotoIcon,
  ArrowRightIcon,
  PencilSquareIcon,
  ChevronRightIcon,
  ComputerDesktopIcon,
  ClipboardDocumentIcon,
  DocumentTextIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  BellAlertIcon,
  EyeIcon,
  ClipboardDocumentCheckIcon,
  IdentificationIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ButtonGroup } from '../components/ui/button-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import { Tip } from '../components/ui/tip';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../components/ui/hover-card';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { NumberInput } from '../components/ui/number-input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { PagoMixtoEditor } from '../components/PagoMixtoEditor';
import { LineaPago, METODO_MIXTO, lineasIniciales, payloadPagos, repartirLineas } from '../utils/cobroMixto';
import WhatsAppIcon from '../components/WhatsAppIcon';
import { disconnectWesSocket, getWesSocket } from '../lib/wesSocket';
import { confirmDialog } from '../components/ConfirmDialog';
import { KmIngresoDialog } from '../components/KmTablero';
import VehicleCombobox from '../components/VehicleCombobox';
import ClientDataDialog from '../components/ClientDataDialog';
import { VehicleServicePanel } from '../components/VehicleServicePanel';
import InformeServicioDialog from '../components/informes/InformeServicioDialog';
import { useRetiro } from '../components/RetiroDialog';

// ============================================================
// Tipos
// ============================================================

interface Station {
  key: string;
  name: string;
  description?: string;
  serviceTypes?: string[];
}

interface QueueItem {
  id: string;
  appointmentCode: string;
  status: string;
  scheduledDate: string;
  checkedInAt?: string;
  pickupNotifiedAt?: string | null;
  priority?: string;
  serviceType?: string;
  client: {
    id?: string | null;
    firstName?: string;
    lastName?: string;
    companyName?: string;
    phone?: string;
    isPlaceholder?: boolean;
  };
  vehicle?: {
    id?: string | null;
    licensePlate?: string | null;
    make?: string | null;
    model?: string | null;
    year?: number | null;
  };
  currentStation: string;
  stationQueue: string[];
  stationsCompleted: string[];
  remitoId?: string | null;
  remitoNumber?: string | null;
  remitoStatus?: string | null;
  services?: Array<{ id?: string; name: string; quantity: number; price?: number; type?: string }>;
  products?: Array<{ id?: string; name: string; quantity: number; price?: number; type?: string }>;
  isReclamo?: boolean;
  reclamoSourceId?: string | null;
  reclamoType?: string | null;
  reclamoNotes?: string | null;
}

interface ServiceReport {
  id: string;
  appointmentId: string;
  appointmentCode?: string;
  status: string;
  startedAt?: string;
  totalCost?: number;
  products?: Array<{ id?: string; name: string; quantity: number; unitPrice?: number; totalPrice?: number; }>;
  services?: Array<{ id?: string; name: string; quantity: number; unitPrice?: number; totalPrice?: number; }>;
  photos?: Array<{ id: string; url: string; description?: string; category?: string; type?: string; driveId?: string; }>;
  notes?: Array<{ note: string; createdAt: string; userName?: string }> | string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  phone?: string;
  make?: string;
  model?: string;
  licensePlate?: string;
  year?: number;
}

interface ProductOption {
  id: string;
  name: string;
  category?: string;
  type?: string;
  salePrice?: number;
}

// ============================================================
// Helpers
// ============================================================

const fullName = (item: any): string => {
  if (!item) return 'Sin cliente';
  if (item.companyName) return item.companyName;
  return `${item.firstName || ''} ${item.lastName || ''}`.trim() || 'Sin nombre';
};

const formatTime = (totalSec: number): string => {
  const s = Math.max(0, Math.floor(totalSec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

const elapsedSeconds = (startedAt?: string | null, now: number = Date.now()): number => {
  if (!startedAt) return 0;
  const t = new Date(startedAt).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.floor((now - t) / 1000);
};

// ============================================================
// Componente principal
// ============================================================

const Workshop: React.FC = () => {
  const [stations, setStations] = useState<Station[]>([]);
  const [queueByStation, setQueueByStation] = useState<Record<string, QueueItem[]>>({});
  const [readyForPickup, setReadyForPickup] = useState<QueueItem[]>([]);
  const [serviceReports, setServiceReports] = useState<Record<string, ServiceReport>>({});
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'board' | 'list'>('list');
  const [listTab, setListTab] = useState<string>('tareas');
  const openOt = (id: string, tab: string = 'tareas') => { setListTab(tab); setSelectedAppointmentId(id); };
  const [tick, setTick] = useState(Date.now());

  // Cronómetro tick global
  useEffect(() => {
    const id = window.setInterval(() => setTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // Fetch principal. Con `silent` no prende el spinner ni avisa si falla: es el
  // refresco de fondo (evento del socket, reconexión, vuelta a la pestaña), la
  // lista se reemplaza en el lugar y no parpadea.
  const fetchAll = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = !!(opts && typeof opts === 'object' && opts.silent === true);
    if (!silent) setLoading(true);
    try {
      const [queueRes, srRes] = await Promise.all([
        axios.get('/api/reception/queue'),
        axios.get('/api/service-reports?status=active')
      ]);
      const stationsData = queueRes.data.stations || [];
      setStations(stationsData);
      setQueueByStation(queueRes.data.queue || {});
      setReadyForPickup(queueRes.data.readyForPickup || []);

      const reports = srRes.data.reports || [];
      const byAppointment: Record<string, ServiceReport> = {};
      for (const r of reports) {
        if (r.appointmentId) byAppointment[r.appointmentId] = r;
      }
      setServiceReports(byAppointment);

    } catch (err: any) {
      console.error('Workshop fetch error:', err);
      if (!silent) toast.error(err.response?.data?.error || 'Error al cargar el taller');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const refreshTimeoutRef = useRef<number | null>(null);
  useEffect(() => {
    fetchAll();
    const socket = getWesSocket();
    const refresh = () => fetchAll({ silent: true });
    // Si el socket se cortó (pestaña dormida, PC suspendida, red caída) los
    // eventos de ese rato se perdieron y la pantalla queda vieja: mostraba una
    // OT "lista para retirar" que ya se había entregado desde otra máquina, y
    // al confirmar el retiro el server la rechazaba. Al reconectar, al volver a
    // la pestaña o al enfocar la ventana se vuelve a pedir la cola.
    const scheduleSilentRefresh = () => {
      if (refreshTimeoutRef.current) window.clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = window.setTimeout(() => { refreshTimeoutRef.current = null; refresh(); }, 400);
    };
    const onVisibilityChange = () => { if (document.visibilityState === 'visible') scheduleSilentRefresh(); };
    socket.on('connect', scheduleSilentRefresh);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', scheduleSilentRefresh);
    socket.on('reception:check-in', refresh);
    socket.on('reception:advance', refresh);
    socket.on('reception:undo', refresh);
    socket.on('reception:deliver', refresh);
    socket.on('reception:notified', refresh);
    socket.on('reception:reopen', refresh);
    socket.on('remito:created', refresh);
    socket.on('wes:update', refresh);
    return () => {
      if (refreshTimeoutRef.current) { window.clearTimeout(refreshTimeoutRef.current); refreshTimeoutRef.current = null; }
      socket.off('connect', scheduleSilentRefresh);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', scheduleSilentRefresh);
      socket.off('reception:check-in', refresh);
      socket.off('reception:advance', refresh);
      socket.off('reception:undo', refresh);
      socket.off('reception:deliver', refresh);
      socket.off('reception:notified', refresh);
      socket.off('reception:reopen', refresh);
      socket.off('remito:created', refresh);
      socket.off('wes:update', refresh);
      disconnectWesSocket();
    };
  }, [fetchAll]);

  // En el teléfono (< lg) el detalle del tablero se abre como modal:
  // el panel lateral quedaba empujado abajo de las columnas y no se veía
  const [isDesktop, setIsDesktop] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Vehículo seleccionado
  const selectedQueueItem = useMemo(() => {
    if (!selectedAppointmentId) return null;
    for (const list of Object.values(queueByStation)) {
      const found = list.find((it) => it.id === selectedAppointmentId);
      if (found) return found;
    }
    // También los "listos para retirar" (no están en queueByStation)
    const ready = readyForPickup.find((it) => it.id === selectedAppointmentId);
    if (ready) return ready;
    return null;
  }, [selectedAppointmentId, queueByStation, readyForPickup]);

  const selectedReport = selectedAppointmentId ? serviceReports[selectedAppointmentId] : null;

  // Contadores para el header
  const allQueueItems = Object.values(queueByStation).flat();
  const totalInProcess = allQueueItems.length;
  const totalOverdue = allQueueItems.filter((it) => {
    const sr = serviceReports[it.id];
    if (!sr?.startedAt) return false;
    const elapsed = Math.floor((tick - new Date(sr.startedAt).getTime()) / 1000);
    return elapsed > ((it as any).estimatedDuration || 60) * 60;
  }).length;

  return (
    // 100dvh: en el celular la barra del navegador achica el viewport dinámicamente;
    // con 100vh el contenido de abajo quedaba cortado
    <div className="flex flex-col h-full min-h-0" style={{ height: 'calc(100dvh - 72px)' }}>
      {/* Header con badges */}
      <div className="flex flex-wrap items-center justify-between gap-2 py-3 px-1 flex-shrink-0">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold">Estado del Taller</h1>
          <div className="inline-flex rounded-lg border border-border overflow-hidden text-xs">
            <button onClick={() => setViewMode('board')} className={`px-2.5 py-1 flex items-center gap-1 ${viewMode === 'board' ? 'bg-foreground text-background' : 'text-muted-foreground hover:bg-accent'}`}>
              <ComputerDesktopIcon className="h-3.5 w-3.5" /> Tablero
            </button>
            <button onClick={() => setViewMode('list')} className={`px-2.5 py-1 flex items-center gap-1 border-l border-border ${viewMode === 'list' ? 'bg-foreground text-background' : 'text-muted-foreground hover:bg-accent'}`}>
              <ClipboardDocumentIcon className="h-3.5 w-3.5" /> Lista
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {readyForPickup.length > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-600 border border-emerald-500/30 px-2.5 py-1 text-xs font-semibold">
              ✓ {readyForPickup.length} listo{readyForPickup.length > 1 ? 's' : ''} para retirar
            </span>
          )}
          {totalOverdue > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-500/15 text-red-500 border border-red-500/30 px-2.5 py-1 text-xs font-semibold">
              ⏱ {totalOverdue} atrasado{totalOverdue > 1 ? 's' : ''}
            </span>
          )}
          <span className="inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground px-2.5 py-1 text-xs font-medium">
            {totalInProcess} en proceso
          </span>
        </div>
      </div>

      {/* Layout: estaciones (izq) + panel detalle (der) */}
      <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0 overflow-y-auto lg:overflow-hidden">

        {/* Columna izquierda: estaciones + listos para retirar */}
        <div className="flex-1 min-w-0 overflow-y-auto space-y-4 pr-1">
          {viewMode === 'list' ? (
            <WorkshopListView
              stations={stations}
              queueByStation={queueByStation}
              readyForPickup={readyForPickup}
              serviceReports={serviceReports}
              selectedAppointmentId={selectedAppointmentId}
              onSelect={openOt}
              onChange={fetchAll}
              tick={tick}
            />
          ) : (
          <>
          {/* Listos para retirar — arriba y destacado */}
          {readyForPickup.length > 0 && (
            <ReadyForPickupSection
              items={readyForPickup}
              serviceReports={serviceReports}
              tick={tick}
              onChange={fetchAll}
            />
          )}

          {/* Estaciones — scroll horizontal si hay muchas */}
          <div className={`grid gap-3 ${
            stations.length === 1 ? 'grid-cols-1' :
            stations.length === 2 ? 'grid-cols-1 sm:grid-cols-2' :
            'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
          }`}>
            {stations.map((station) => (
              <StationColumn
                key={station.key}
                station={station}
                items={queueByStation[station.key] || []}
                serviceReports={serviceReports}
                tick={tick}
                selectedAppointmentId={selectedAppointmentId}
                onSelect={setSelectedAppointmentId}
                onChange={fetchAll}
              />
            ))}
          </div>
          </>
          )}

          {!loading && viewMode === 'board' && totalInProcess === 0 && readyForPickup.length === 0 && (
            <div className="text-center py-16 text-muted-foreground">
              <TruckIcon className="h-12 w-12 mx-auto mb-2 opacity-30" />
              <p>No hay vehículos en el taller</p>
              <p className="text-xs mt-1">Recepcioná un turno desde Turnos para verlo acá</p>
            </div>
          )}
        </div>

        {/* Panel lateral — solo en modo tablero y en desktop (en el teléfono va como modal) */}
        {viewMode === 'board' && (selectedAppointmentId && selectedQueueItem && isDesktop ? (
          <div className={`w-full ${stations.length >= 3 ? 'lg:w-[360px]' : 'lg:w-[440px]'} flex-shrink-0 overflow-y-auto`}>
            <VehicleDetailPanel
              queueItem={selectedQueueItem}
              report={selectedReport}
              stations={stations}
              tick={tick}
              onClose={() => setSelectedAppointmentId(null)}
              onChange={fetchAll}
            />
          </div>
        ) : (
          <div className="w-[440px] flex-shrink-0 hidden lg:flex items-center justify-center text-muted-foreground text-sm border border-dashed border-border rounded-xl">
            <div className="text-center">
              <TruckIcon className="h-8 w-8 mx-auto mb-2 opacity-20" />
              <p className="text-xs">Seleccioná una OT para ver el detalle</p>
            </div>
          </div>
        ))}
      </div>

      {/* Tablero en el teléfono: el detalle se abre como modal */}
      {viewMode === 'board' && !isDesktop && selectedAppointmentId && selectedQueueItem && (
        <VehicleDetailPanel
          modalOnly
          queueItem={selectedQueueItem}
          report={selectedReport}
          stations={stations}
          tick={tick}
          onClose={() => setSelectedAppointmentId(null)}
          onChange={fetchAll}
        />
      )}

      {/* En modo lista, la OT seleccionada se abre como modal (sin panel lateral) */}
      {viewMode === 'list' && selectedAppointmentId && selectedQueueItem && (
        <VehicleDetailPanel
          modalOnly
          initialTab={listTab}
          queueItem={selectedQueueItem}
          report={selectedReport}
          stations={stations}
          tick={tick}
          onClose={() => setSelectedAppointmentId(null)}
          onChange={fetchAll}
        />
      )}
    </div>
  );
};

// ============================================================
// Vista de lista (tabla de OTs)
// ============================================================

const STATUS_LABEL: Record<string, { label: string; cls: string; dot: string }> = {
  in_progress: { label: 'En proceso', cls: 'bg-emerald-500/15 text-emerald-600', dot: 'bg-emerald-500' },
  paused: { label: 'Pausado', cls: 'bg-red-500/15 text-red-600', dot: 'bg-red-500' },
  checked_in: { label: 'Pendiente', cls: 'bg-blue-500/15 text-blue-600', dot: 'bg-blue-500' },
  waiting_parts: { label: 'Esperando repuestos', cls: 'bg-orange-500/15 text-orange-600', dot: 'bg-orange-500' },
  ready_for_pickup: { label: 'Listo para retirar', cls: 'bg-emerald-500/15 text-emerald-600', dot: 'bg-emerald-500' },
};

// Botón de acción con tooltip de shadcn (estilo del resto del sistema)
// "Grumaq S R L · AB123CD": para el cartel de quién retira
const subtituloRetiro = (it: QueueItem) => {
  const cliente = it.client?.companyName || `${it.client?.firstName || ''} ${it.client?.lastName || ''}`.trim();
  return [cliente, it.vehicle?.licensePlate, it.appointmentCode].filter(Boolean).join(' · ');
};

const ActBtn: React.FC<{ label: string; onClick: (e: React.MouseEvent) => void; disabled?: boolean; className?: string; children: React.ReactNode }> = ({ label, onClick, disabled, className, children }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button variant="outline" size="icon" className={`size-8 ${className || ''}`} disabled={disabled} onClick={onClick}>{children}</Button>
    </TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
);

const WorkshopListView: React.FC<{
  stations: Station[];
  queueByStation: Record<string, QueueItem[]>;
  readyForPickup: QueueItem[];
  serviceReports: Record<string, ServiceReport>;
  selectedAppointmentId: string | null;
  onSelect: (id: string, tab?: string) => void;
  onChange: () => void;
  tick: number;
}> = ({ stations, queueByStation, readyForPickup, serviceReports, selectedAppointmentId, onSelect, onChange, tick }) => {
  const timeColor = (secs: number) => secs < 3600 ? 'text-emerald-600' : secs < 7200 ? 'text-amber-600' : 'text-red-600';
  const navigate = useNavigate();
  const [filter, setFilter] = useState<string>('all');
  const [busy, setBusy] = useState<string | null>(null);
  const { pedirRetiro, dialogoRetiro } = useRetiro();
  const { user, hasPermission } = useAuth();
  // El operario trabaja las OTs pero no gestiona clientes ni les habla por WhatsApp
  const isOperario = user?.role === 'operario';
  const canEditClients = hasPermission('clients');
  // Completar/corregir los datos del cliente de una OT (o vincular un "Sin datos" a un cliente real)
  const [clientDataFor, setClientDataFor] = useState<string | null>(null);
  // OT que se quiso completar sin km: se pide acá (crear el turno ya no lo exige, 21/09/2026)
  const [kmCompletar, setKmCompletar] = useState<QueueItem | null>(null);
  // Informe de servicio de una OT lista para retirar (v12.3.1)
  const [informeListaId, setInformeListaId] = useState<string | null>(null);
  // El botón solo aparece donde FALTA algo (placeholder o sin teléfono)
  const needsClientData = (it: QueueItem) =>
    canEditClients && !!it.client?.id &&
    (!!it.client?.isPlaceholder || !String(it.client?.phone || '').trim());

  // Checklist de diagnóstico de la OT: lo busca o lo crea (hereda cliente/vehículo/km)
  const openDiagnosis = async (it: QueueItem) => {
    try {
      const res = await axios.post('/api/diagnostics/for-appointment', { appointmentId: it.id });
      navigate(`/diagnostics/${res.data.diagnosis.id}/checklist`);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'No se pudo abrir el diagnóstico');
    }
  };

  // Acta de recepción: estado del auto al ingresar (fotos + conformidad).
  // Una sola por OT: si ya existe se abre esa.
  const openActa = async (it: QueueItem) => {
    try {
      const res = await axios.post('/api/receptions', {
        appointmentId: it.id,
        clientId: it.client?.id || null,
        vehicleId: (it as any).vehicle?.id || null
      });
      navigate(`/actas/${res.data.acta.id}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'No se pudo abrir el acta');
    }
  };

  // Resumen semáforo del checklist por OT (badge en tarjeta/tabla)
  // `previo` = último checklist del mismo vehículo en otra visita; `vigente` si
  // tiene menos de N días (Configuración). Colorea el botón, no bloquea.
  interface DiagPrevio { diagnosisId: string; appointmentId: string | null; fecha: string; dias: number; items: number; urgente: number; atencion: number }
  const [diagSummaries, setDiagSummaries] = useState<Record<string, { diagnosisId: string | null; urgente: number; atencion: number; items: number; previo?: DiagPrevio; vigente?: boolean; validityDays?: number }>>({});
  // Checklist vigente de otra visita y esta OT todavía sin el suyo
  const checklistVigente = (apptId: string) => {
    const s = diagSummaries[apptId];
    return Boolean(s && s.vigente && s.previo && !(s.items > 0)) ? s!.previo! : null;
  };
  const labelChecklist = (apptId: string) => {
    const p = checklistVigente(apptId);
    if (!p) return 'Checklist de diagnóstico';
    const s = diagSummaries[apptId];
    const restan = Math.max(0, (s?.validityDays ?? 60) - p.dias);
    return `Checklist vigente: hecho hace ${p.dias === 0 ? 'hoy' : `${p.dias} día${p.dias === 1 ? '' : 's'}`} (vale ${restan} día${restan === 1 ? '' : 's'} más). Tocá para hacer uno nuevo igual.`;
  };
  const claseVigente = 'border-emerald-500 text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300';
  useEffect(() => {
    const ids = [...Object.values(queueByStation).flat(), ...readyForPickup].map((q: any) => q?.id).filter(Boolean);
    if (!ids.length) { setDiagSummaries({}); return; }
    axios.post('/api/diagnostics/summary-by-appointments', { ids })
      .then(res => setDiagSummaries(res.data.summaries || {}))
      .catch(() => {});
  }, [queueByStation, readyForPickup]);

  // Chip clickeable con el resultado del checklist (rojo/amarillo o todo OK)
  const DiagChip = ({ apptId }: { apptId: string }) => {
    const s = diagSummaries[apptId];
    // Sin checklist propio: mostrar el vigente de la visita anterior (con su antigüedad)
    if ((!s || !s.items) && s?.previo && s.vigente) {
      const p = s.previo;
      return (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); navigate(`/diagnostics/${p.diagnosisId}/checklist`); }}
          className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/50 bg-emerald-500/10 px-2 py-0.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-300"
        >
          <ClipboardDocumentCheckIcon className="h-3.5 w-3.5" />
          <span>hace {p.dias === 0 ? 'hoy' : `${p.dias} d`}</span>
          {p.urgente > 0 && <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400"><span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500" />{p.urgente}</span>}
          {p.atencion > 0 && <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400"><span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />{p.atencion}</span>}
        </button>
      );
    }
    if (!s || !s.items) return null;
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); navigate(`/diagnostics/${s.diagnosisId}/checklist`); }}
        className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2 py-0.5 text-xs font-semibold hover:bg-accent"
      >
        <ClipboardDocumentCheckIcon className="h-3.5 w-3.5" />
        {s.urgente > 0 && <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400"><span className="inline-block h-1.5 w-1.5 rounded-full bg-red-500" />{s.urgente}</span>}
        {s.atencion > 0 && <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400"><span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />{s.atencion}</span>}
        {s.urgente === 0 && s.atencion === 0 && <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />OK</span>}
      </button>
    );
  };
  // Cargar/asignar patente a un turno que quedó sin vehículo. Se puede elegir un
  // vehículo YA cargado del cliente, o crear uno nuevo tipeando la patente.
  const [plateFor, setPlateFor] = useState<QueueItem | null>(null);
  const [clientVehicles, setClientVehicles] = useState<any[]>([]);
  const [pickedVehicle, setPickedVehicle] = useState<any | null>(null);
  const [newPlate, setNewPlate] = useState('');
  const [plateSaving, setPlateSaving] = useState(false);
  // Remito en borrador al entregar el auto: se ofrece emitirlo en el momento
  const [remitoPend, setRemitoPend] = useState<any | null>(null);
  const [remitoMetodo, setRemitoMetodo] = useState('');
  // Cobro mixto al retirar: un renglón por medio (recargo solo sobre la parte con tarjeta)
  const [pagosMixtos, setPagosMixtos] = useState<LineaPago[]>([]);
  const [remitoEmitiendo, setRemitoEmitiendo] = useState(false);
  const [metodosPago, setMetodosPago] = useState<{ code: string; name: string; surcharge?: number }[]>([]);
  useEffect(() => {
    axios.get('/api/settings/payment-methods')
      .then((r) => setMetodosPago(Array.isArray(r.data) ? r.data.filter((m: any) => m.enabled !== 0) : []))
      .catch(() => setMetodosPago([]));
  }, []);
  const money = (n: number) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(Number(n) || 0);
  // Recargo del método elegido (tarjetas): mismo criterio que Remitos —
  // los porcentajes negativos hoy no se aplican en ningún lado del sistema
  const remitoBase = Number(remitoPend?.estimatedValue) || 0;
  const recargoPct = Number(metodosPago.find((m) => m.code === remitoMetodo)?.surcharge) || 0;
  const esPagoMixto = remitoMetodo === METODO_MIXTO;
  const repartoMixto = esPagoMixto
    ? repartirLineas(remitoBase, pagosMixtos, Object.fromEntries(metodosPago.map((m) => [m.code, Number(m.surcharge) || 0])))
    : null;
  const remitoRecargo = repartoMixto
    ? repartoMixto.recargoTotal
    : (recargoPct > 0 ? Math.round(remitoBase * (1 + recargoPct / 100)) - remitoBase : 0);
  const remitoTotal = remitoBase + remitoRecargo;
  const emitirRemitoPend = async () => {
    if (!remitoPend) return;
    if (!remitoMetodo) { toast.error('Elegí cómo pagó el cliente'); return; }
    if (repartoMixto && !repartoMixto.ok) { toast.error(repartoMixto.error || 'Revisá el pago mixto'); return; }
    setRemitoEmitiendo(true);
    try {
      await axios.post(`/api/remitos/${remitoPend.id}/issue`, repartoMixto ? { payments: payloadPagos(pagosMixtos) } : { paymentMethod: remitoMetodo });
      toast.success(`Remito ${remitoPend.remitNumber} emitido`);
      setRemitoPend(null);
      onChange();
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'No se pudo emitir el remito');
    } finally { setRemitoEmitiendo(false); }
  };
  const openPlate = (it: QueueItem) => {
    setPlateFor(it); setPickedVehicle(null); setNewPlate(''); setClientVehicles([]);
    axios.get(`/api/vehicles/for-appointment/${it.id}`)
      .then((r) => setClientVehicles(Array.isArray(r.data) ? r.data : []))
      .catch(() => setClientVehicles([]));
  };
  // El server rechazó por estado: la OT cambió desde otra máquina y esta
  // pantalla quedó vieja. Se vuelve a pedir la cola para mostrar lo real.
  const esRechazoPorEstado = (e: any) => [400, 404, 409].includes(Number(e?.response?.status));
  const run = async (id: string, fn: () => Promise<any>, okMsg?: string) => {
    setBusy(id);
    try { await fn(); if (okMsg) toast.success(okMsg); onChange(); }
    catch (e: any) { toast.error(e.response?.data?.error || 'Error'); if (esRechazoPorEstado(e)) onChange(); }
    finally { setBusy(null); }
  };
  const submitPlate = async () => {
    if (!plateFor) return;
    const plate = String(pickedVehicle?.plate || newPlate).toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!plate) { toast.error('Elegí un vehículo o ingresá la patente'); return; }
    setPlateSaving(true);
    try {
      await axios.post('/api/vehicles/attach-plate', { appointmentId: plateFor.id, plate });
      toast.success('Vehículo asignado');
      setPlateFor(null); setPickedVehicle(null); setNewPlate('');
      onChange();
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'No se pudo asignar el vehículo');
    } finally { setPlateSaving(false); }
  };
  const startOt = (it: QueueItem) => run(it.id, () => axios.patch(`/api/appointments-enhanced/${it.id}`, { status: 'in_progress' }), 'Servicio iniciado');
  const pauseOt = (it: QueueItem) => { const r = serviceReports[it.id]; if (!r) { toast.error('Sin service report'); return; } run(it.id, () => axios.put(`/api/service-reports/${r.id}`, { status: 'paused' }), 'Pausado'); };
  const resumeOt = (it: QueueItem) => { const r = serviceReports[it.id]; run(it.id, () => r ? axios.put(`/api/service-reports/${r.id}`, { status: 'in_progress' }) : axios.patch(`/api/appointments-enhanced/${it.id}`, { status: 'in_progress' }), 'Reanudado'); };
  // Completar: si la OT tiene vehículo y sigue sin km, el server avisa una vez
  // (KM_MISSING) y acá se ofrece cargarlo o completar sin km (es opcional,
  // 22/09/2026). No usa `run` porque ese aviso no es un toast: abre el diálogo
  // y reintenta con el km o con sinKm.
  const postCompletar = async (it: QueueItem, km?: number, sinKm = false) => {
    setBusy(it.id);
    try {
      await axios.post(`/api/reception/${it.id}/complete-work`, km != null ? { mileage: km } : sinKm ? { sinKm: true } : {});
      toast.success('Trabajo terminado');
      onChange();
    } catch (e: any) {
      if (e.response?.data?.code === 'KM_MISSING') { setKmCompletar(it); return; }
      toast.error(e.response?.data?.error || 'Error');
      if (esRechazoPorEstado(e)) onChange();
    } finally { setBusy(null); }
  };
  const completeOt = async (it: QueueItem) => { if (!(await confirmDialog({ title: 'Completar trabajo', description: 'El vehículo pasará a "Listo para retirar".', confirmText: 'Completar' }))) return; postCompletar(it); };
  const notifyOt = (it: QueueItem) => run(it.id, () => axios.post(`/api/reception/${it.id}/notify-ready`), 'Cliente avisado');
  // Cancelar toda la OT: el auto se va sin que se le haga nada. Se avisa qué
  // pasa con el remito, que es lo que puede quedar colgado.
  const cancelOt = async (it: QueueItem) => {
    const conRemito = it.remitoId && it.remitoStatus !== 'cancelled';
    const emitido = it.remitoStatus === 'issued';
    if (!(await confirmDialog({
      title: `Cancelar la ${it.appointmentCode}`,
      description: emitido
        ? `Esta OT ya tiene el remito ${it.remitoNumber} EMITIDO. Cancelar la OT no lo anula: si el trabajo no se hizo, anulá el remito aparte.`
        : conRemito
          ? `Se cancela la OT y sale del taller. El remito ${it.remitoNumber} está en borrador y queda como está.`
          : 'Se cancela la OT y sale del taller. Se puede reabrir después desde Turnos.',
      confirmText: 'Cancelar la OT'
    }))) return;
    run(it.id, () => axios.patch(`/api/appointments-enhanced/${it.id}`, { status: 'cancelled' }), 'OT cancelada');
  };
  const deliverOt = async (it: QueueItem) => {
    // Clientes que piden quién retira (Grumaq): el cartel con nombre y DNI ya es la confirmación
    const retiro = await pedirRetiro(it.id, subtituloRetiro(it));
    if (retiro === undefined) return;
    if (retiro === null && !(await confirmDialog({ title: 'Confirmar retiro', description: 'Se marcará la OT como entregada y pasará a "Completada".', confirmText: 'Confirmar retiro' }))) return;
    setBusy(it.id);
    try {
      const res = await axios.post(`/api/reception/${it.id}/deliver`, retiro ? { retiro } : {});
      toast.success('Vehículo entregado');
      onChange();
      // El auto se va: si el remito quedó en borrador, es el momento de emitirlo
      const pend = res.data?.pendingRemito;
      if (pend?.id) {
        const cli = it.client?.companyName || `${it.client?.firstName || ''} ${it.client?.lastName || ''}`.trim();
        setRemitoPend({ ...pend, cliente: cli, code: it.appointmentCode });
        setRemitoMetodo(pend.paymentMethod || '');
      }
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Error');
      if (esRechazoPorEstado(e)) onChange();
    } finally { setBusy(null); }
  };
  const stationLabel = (key: string) => stations.find((s) => s.key === key)?.name || key;
  const seen = new Set<string>();
  const allItems = [...Object.values(queueByStation).flat(), ...readyForPickup]
    .filter((it) => it && it.id && !seen.has(it.id) && !!seen.add(it.id));
  const items = filter === 'all' ? allItems : allItems.filter((it) => it.currentStation === filter);
  const chip = (active: boolean) => `inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${active ? 'bg-foreground text-background border-transparent font-medium' : 'border-border hover:bg-accent text-muted-foreground'}`;
  const countCls = (active: boolean) => `text-[10px] leading-none px-1.5 py-0.5 rounded-full ${active ? 'bg-background/25' : 'bg-muted-foreground/15'}`;

  return (
    <div className="space-y-3">
      {dialogoRetiro}
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-1 md:flex-wrap md:overflow-visible md:pb-0">
        <button onClick={() => setFilter('all')} className={`shrink-0 ${chip(filter === 'all')}`}>Todas<span className={countCls(filter === 'all')}>{allItems.length}</span></button>
        {stations.map((s) => {
          const n = allItems.filter((it) => it.currentStation === s.key).length;
          const active = filter === s.key;
          return <button key={s.key} onClick={() => setFilter(s.key)} className={`shrink-0 ${chip(active)}`}>{s.name}<span className={countCls(active)}>{n}</span></button>;
        })}
      </div>
      {items.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <TruckIcon className="h-12 w-12 mx-auto mb-2 opacity-30" />
          <p>No hay vehículos {filter === 'all' ? 'en el taller' : 'en esta estación'}</p>
        </div>
      ) : (
      <>
      {/* Teléfono: tarjetas por OT (la tabla escondía vehículo, estación y tiempo) */}
      <div className="md:hidden space-y-2.5">
        {items.map((it) => {
          const r = serviceReports[it.id];
          const effStatus = (r && r.status === 'paused' && it.status === 'in_progress') ? 'paused' : it.status;
          const st = STATUS_LABEL[effStatus] || { label: effStatus, cls: 'bg-muted text-muted-foreground', dot: 'bg-muted-foreground' };
          const vPlate = it.vehicle?.licensePlate || r?.licensePlate || '';
          const vMake = it.vehicle?.make || r?.make || '';
          const vModel = it.vehicle?.model || r?.model || '';
          const vehDesc = [vMake, vModel].filter(Boolean).join(' ');
          const clienteName = fullName(it.client) || fullName(r) || '-';
          const clienteDisplay = clienteName === 'Sin datos' && vPlate ? `Sin datos · ${vPlate}` : clienteName;
          const phone = (it.client?.phone || '').replace(/\D/g, '');
          const secs = effStatus === 'in_progress'
            ? (r?.startedAt ? elapsedSeconds(r.startedAt, tick) : (it.checkedInAt ? Math.floor((tick - new Date(it.checkedInAt).getTime()) / 1000) : 0))
            : null;
          return (
            <div
              key={it.id}
              onClick={() => onSelect(it.id)}
              className={`rounded-xl border border-border p-3 space-y-2 active:bg-accent ${selectedAppointmentId === it.id ? 'bg-accent' : 'bg-card'}`}
            >
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full shrink-0 ${st.dot} ${effStatus === 'in_progress' ? 'animate-pulse' : ''}`} />
                <span className="font-mono font-semibold">{it.appointmentCode}</span>
                {it.isReclamo && <span>🚨</span>}
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${st.cls}`}>{st.label}</span>
                {secs !== null && (
                  <span className={`ml-auto font-mono text-sm font-semibold tabular-nums ${timeColor(secs)}`}>{formatTime(secs)}</span>
                )}
              </div>
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">{clienteDisplay}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {vPlate && <span className="font-medium text-foreground">{vPlate}</span>}
                  {vehDesc && <span>{vPlate ? ' · ' : ''}{vehDesc}</span>}
                  {(vPlate || vehDesc) && ' · '}
                  {stationLabel(it.currentStation)}
                </div>
              </div>
              {diagSummaries[it.id]?.items ? (
                <div onClick={(e) => e.stopPropagation()}>
                  <DiagChip apptId={it.id} />
                </div>
              ) : null}
              <div className="flex flex-wrap items-center gap-2 pt-1" onClick={(e) => e.stopPropagation()}>
                {effStatus === 'checked_in' && (
                  <Button className="h-11 px-4 text-sm" disabled={busy === it.id} onClick={() => startOt(it)}>
                    <PlayIcon className="h-5 w-5 mr-1.5" />Iniciar
                  </Button>
                )}
                {effStatus === 'in_progress' && (
                  <>
                    <Button className="h-11 px-4 text-sm" disabled={busy === it.id} onClick={() => completeOt(it)}>
                      <CheckIcon className="h-5 w-5 mr-1.5" />Completar
                    </Button>
                    <Button variant="outline" className="h-11 px-4 text-sm" disabled={busy === it.id} onClick={() => pauseOt(it)}>
                      <PauseIcon className="h-5 w-5 mr-1.5" />Pausar
                    </Button>
                  </>
                )}
                {effStatus === 'paused' && (
                  <Button className="h-11 px-4 text-sm" disabled={busy === it.id} onClick={() => resumeOt(it)}>
                    <PlayIcon className="h-5 w-5 mr-1.5" />Reanudar
                  </Button>
                )}
                {effStatus === 'ready_for_pickup' && !isOperario && (
                  <>
                    <Button variant="outline" className="h-11 px-4 text-sm" disabled={busy === it.id} onClick={() => notifyOt(it)}>
                      <BellAlertIcon className="h-5 w-5 mr-1.5" />Avisar
                    </Button>
                    <Button className="h-11 px-4 text-sm" disabled={busy === it.id} onClick={() => deliverOt(it)}>
                      <TruckIcon className="h-5 w-5 mr-1.5" />Retiro
                    </Button>
                    <Button variant="outline" className="h-11 px-4 text-sm" onClick={() => setInformeListaId(it.id)}>
                      <DocumentTextIcon className="h-5 w-5 mr-1.5" />Informe
                    </Button>
                  </>
                )}
                <div className="ml-auto flex items-center gap-2">
                  {!vPlate && (
                    <Button variant="outline" size="icon" className="size-11" aria-label="Cargar patente" onClick={() => openPlate(it)}>
                      <PlusIcon className="h-5 w-5" />
                    </Button>
                  )}
                  {needsClientData(it) && (
                    <Button variant="outline" size="icon"
                      className="size-11 border-amber-500 text-amber-600"
                      aria-label="Faltan datos del cliente" onClick={() => setClientDataFor(it.client!.id!)}>
                      <IdentificationIcon className="h-5 w-5" />
                    </Button>
                  )}
                  {!isOperario && (
                    <Button variant="outline" size="icon" className="size-11" onClick={() => onSelect(it.id, 'tareas')} aria-label="Servicios">
                      <WrenchScrewdriverIcon className="h-5 w-5" />
                    </Button>
                  )}
                  <Tip label={labelChecklist(it.id)}>
                    <Button variant="outline" size="icon" className={`size-11 ${checklistVigente(it.id) ? claseVigente : ''}`} onClick={() => openDiagnosis(it)} aria-label="Checklist de diagnóstico">
                      <ClipboardDocumentCheckIcon className="h-5 w-5" />
                    </Button>
                  </Tip>
                  {!isOperario && (
                    <Button variant="outline" size="icon" className="size-11" aria-label="WhatsApp"
                      onClick={() => { if (phone) window.open(`https://wa.me/${phone}`, '_blank'); else toast.error('Sin teléfono del cliente'); }}>
                      <WhatsAppIcon className="h-5 w-5" />
                    </Button>
                  )}
                  {!isOperario && (
                    <Button variant="outline" size="icon" className="size-11 text-destructive"
                      aria-label="Cancelar la OT" disabled={busy === it.id} onClick={() => cancelOt(it)}>
                      <XMarkIcon className="h-5 w-5" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Desktop: tabla como siempre */}
      <div className="hidden md:block rounded-xl border border-border overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-muted-foreground">
          <tr className="text-left">
            <th className="px-3 py-2 font-medium whitespace-nowrap">OT</th>
            <th className="px-3 py-2 font-medium">Cliente</th>
            <th className="hidden md:table-cell px-3 py-2 font-medium">Vehículo</th>
            <th className="hidden md:table-cell px-3 py-2 font-medium whitespace-nowrap">Estación</th>
            <th className="px-3 py-2 font-medium whitespace-nowrap">Estado</th>
            <th className="hidden lg:table-cell px-3 py-2 font-medium whitespace-nowrap">Tiempo</th>
            <th className="px-3 py-2 font-medium text-center">Checklist</th>
            <th className="px-3 py-2 font-medium text-right">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => {
            const r = serviceReports[it.id];
            const effStatus = (r && r.status === 'paused' && it.status === 'in_progress') ? 'paused' : it.status;
            const st = STATUS_LABEL[effStatus] || { label: effStatus, cls: 'bg-muted text-muted-foreground', dot: 'bg-muted-foreground' };
            // Vehiculo: primero del turno (siempre disponible), luego del service report
            const vPlate = it.vehicle?.licensePlate || r?.licensePlate || '';
            const vMake = it.vehicle?.make || r?.make || '';
            const vModel = it.vehicle?.model || r?.model || '';
            const vYear = it.vehicle?.year || r?.year || '';
            const vehDesc = [vMake, vModel].filter(Boolean).join(' ');
            const vehTip = [vPlate, vehDesc, vYear && `(${vYear})`].filter(Boolean).join(' · ') || 'Sin vehículo asociado';
            const clienteName = fullName(it.client) || fullName(r) || '-';
            // Para "Sin datos", mostrar la patente al lado para identificarlo
            const clienteDisplay = clienteName === 'Sin datos' && vPlate ? `Sin datos · ${vPlate}` : clienteName;
            const phone = (it.client?.phone || '').replace(/\D/g, '');
            return (
              <tr key={it.id} onClick={() => onSelect(it.id)}
                className={`border-t border-border cursor-pointer hover:bg-accent ${selectedAppointmentId === it.id ? 'bg-accent' : ''}`}>
                <td className="px-3 py-2 font-mono whitespace-nowrap">
                  <span className="inline-flex items-center gap-2">
                    <Tip label={st.label}><span className={`w-2 h-2 rounded-full shrink-0 ${st.dot} ${effStatus === 'in_progress' ? 'animate-pulse' : ''}`} /></Tip>
                    {it.appointmentCode}{it.isReclamo && <Tip label="Reclamo"><span className="ml-1">🚨</span></Tip>}
                  </span>
                </td>
                <td className="px-3 py-2 truncate max-w-[160px]">{clienteDisplay}</td>
                <td className="hidden md:table-cell px-3 py-2 truncate max-w-[220px]">
                  {vPlate || vehDesc ? (
                    <Tip label={vehTip}>
                      <span className="cursor-default">
                        {vPlate && <span className="font-medium">{vPlate}</span>}
                        {vehDesc && <span className="text-muted-foreground">{vPlate ? ' · ' : ''}{vehDesc}</span>}
                      </span>
                    </Tip>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="hidden md:table-cell px-3 py-2 whitespace-nowrap">{stationLabel(it.currentStation)}</td>
                <td className="px-3 py-2"><span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${st.cls}`}>{st.label}</span></td>
                <td className="hidden lg:table-cell px-3 py-2 whitespace-nowrap tabular-nums font-mono">
                  {(() => {
                    if (effStatus !== 'in_progress') return <span className="text-muted-foreground">—</span>;
                    const secs = r?.startedAt ? elapsedSeconds(r.startedAt, tick) : (it.checkedInAt ? Math.floor((tick - new Date(it.checkedInAt).getTime()) / 1000) : 0);
                    return <span className={`font-semibold ${timeColor(secs)}`}>{formatTime(secs)}</span>;
                  })()}
                </td>
                <td className="px-3 py-2 text-center" onClick={(e) => { if (diagSummaries[it.id]?.items) e.stopPropagation(); }}>
                  <div className="flex items-center justify-center gap-1.5">
                    <DiagChip apptId={it.id} />
                    {!diagSummaries[it.id]?.items && <span className="text-muted-foreground">—</span>}
                  </div>
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end">
                    <ButtonGroup>
                      {effStatus === 'checked_in' && (
                        <ActBtn label="Iniciar" disabled={busy === it.id} onClick={() => startOt(it)}><PlayIcon className="h-4 w-4" /></ActBtn>
                      )}
                      {effStatus === 'in_progress' && (
                        <>
                          <ActBtn label="Completar" disabled={busy === it.id} onClick={() => completeOt(it)}><CheckIcon className="h-4 w-4" /></ActBtn>
                          <ActBtn label="Pausar" disabled={busy === it.id} onClick={() => pauseOt(it)}><PauseIcon className="h-4 w-4" /></ActBtn>
                        </>
                      )}
                      {effStatus === 'paused' && (
                        <ActBtn label="Reanudar" disabled={busy === it.id} onClick={() => resumeOt(it)}><PlayIcon className="h-4 w-4" /></ActBtn>
                      )}
                      {effStatus === 'ready_for_pickup' && !isOperario && (
                        <>
                          <ActBtn label="Avisar al cliente" disabled={busy === it.id} onClick={() => notifyOt(it)}><BellAlertIcon className="h-4 w-4" /></ActBtn>
                          <ActBtn label="Confirmar retiro" disabled={busy === it.id} onClick={() => deliverOt(it)}><TruckIcon className="h-4 w-4" /></ActBtn>
                          <ActBtn label="Informe de servicio (km, trabajos y revisión)" onClick={() => setInformeListaId(it.id)}><DocumentTextIcon className="h-4 w-4" /></ActBtn>
                        </>
                      )}
                      {!vPlate && (
                        <ActBtn label="Cargar patente" onClick={() => openPlate(it)}><PlusIcon className="h-4 w-4" /></ActBtn>
                      )}
                      {needsClientData(it) && (
                        <ActBtn label="Faltan datos del cliente — completar o vincular a uno existente" onClick={() => setClientDataFor(it.client!.id!)}>
                          <IdentificationIcon className="h-4 w-4 text-amber-500" />
                        </ActBtn>
                      )}
                      {!isOperario && (
                        <ActBtn label="Agregar / quitar servicios" onClick={() => onSelect(it.id, 'tareas')}><WrenchScrewdriverIcon className="h-4 w-4" /></ActBtn>
                      )}
                      <ActBtn label={labelChecklist(it.id)} className={checklistVigente(it.id) ? claseVigente : ''} onClick={() => openDiagnosis(it)}><ClipboardDocumentCheckIcon className="h-4 w-4" /></ActBtn>
                      <ActBtn label="Acta de recepción (fotos del estado)" onClick={() => openActa(it)}><CameraIcon className="h-4 w-4" /></ActBtn>
                      {!isOperario && (
                        <ActBtn label="WhatsApp al cliente" onClick={() => { if (phone) window.open(`https://wa.me/${phone}`, '_blank'); else toast.error('Sin teléfono del cliente'); }}><WhatsAppIcon className="h-4 w-4" /></ActBtn>
                      )}
                      {!isOperario && (
                        <ActBtn label="Cancelar la OT" disabled={busy === it.id} onClick={() => cancelOt(it)}>
                          <XMarkIcon className="h-4 w-4 text-destructive" />
                        </ActBtn>
                      )}
                    </ButtonGroup>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      </>
      )}

      <InformeServicioDialog
        appointmentId={informeListaId}
        open={!!informeListaId}
        onOpenChange={(o) => { if (!o) setInformeListaId(null); }}
      />

      {/* Cargar/asignar vehículo a un turno sin vehículo */}
      {/* El auto se entregó y su remito quedó en borrador: emitirlo ahora es
          lo que registra la venta y descuenta el stock. Se puede postergar. */}
      <Dialog open={!!remitoPend} onOpenChange={(o) => { if (!o) setRemitoPend(null); }}>
        <DialogContent className="flex h-[92vh] max-h-[92vh] w-[96vw] max-w-6xl flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-xl">Emitir el remito {remitoPend?.remitNumber}</DialogTitle>
            <DialogDescription>
              {remitoPend?.code ? `${remitoPend.code} · ` : ''}Quedó en borrador: si no lo emitís, la venta no se registra y el stock no se descuenta.
            </DialogDescription>
          </DialogHeader>
          <div className="grid flex-1 min-h-0 gap-5 overflow-y-auto md:grid-cols-[1.4fr_1fr]">
            {/* Izquierda: qué se entrega */}
            <div className="flex min-h-0 flex-col gap-3">
              <div className="rounded-xl border p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Cliente</p>
                <p className="text-lg font-semibold leading-tight">{remitoPend?.cliente || 'Sin cliente'}</p>
                {(remitoPend?.licensePlate || remitoPend?.model) && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    {[remitoPend?.licensePlate, [remitoPend?.make, remitoPend?.model].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>
              <div className="flex min-h-0 flex-1 flex-col rounded-xl border">
                <div className="flex items-center justify-between border-b px-4 py-2.5">
                  <span className="text-sm font-medium">Detalle del remito</span>
                  <span className="text-xs text-muted-foreground">{remitoPend?.items?.length || 0} ítem(s)</span>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto">
                  {(remitoPend?.items?.length || 0) > 0 ? (
                    <table className="w-full text-sm">
                      <tbody>
                        {remitoPend.items.map((it: any, i: number) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="w-10 py-2 pl-4 pr-2 text-muted-foreground tabular-nums">{Number(it.quantity) || 1}×</td>
                            <td className="py-2 pr-2">{it.nombre}</td>
                            <td className="py-2 pr-4 text-right tabular-nums">{money(it.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="p-4 text-sm text-muted-foreground">El remito no tiene ítems cargados.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Derecha: cómo se cobra */}
            <div className="flex flex-col gap-3">
              <div className="space-y-2">
                <Label className="text-sm">¿Cómo pagó?</Label>
                <Select
                  value={remitoMetodo}
                  onValueChange={(v) => {
                    setRemitoMetodo(v);
                    if (v === METODO_MIXTO) setPagosMixtos((prev) => (prev.length ? prev : lineasIniciales()));
                  }}
                >
                  <SelectTrigger className="h-11"><SelectValue placeholder="Elegir método de pago" /></SelectTrigger>
                  <SelectContent>
                    {metodosPago.map((m) => (
                      <SelectItem key={m.code} value={m.code}>
                        {m.name}{Number(m.surcharge) > 0 ? ` (+${Number(m.surcharge)}%)` : ''}
                      </SelectItem>
                    ))}
                    <SelectItem value={METODO_MIXTO}>Pago mixto (más de un medio)</SelectItem>
                  </SelectContent>
                </Select>
                {esPagoMixto && (
                  <PagoMixtoEditor
                    base={remitoBase}
                    lineas={pagosMixtos}
                    onChange={setPagosMixtos}
                    opciones={metodosPago.map((m) => ({ value: m.code, label: m.name, surcharge: Number(m.surcharge) || 0 }))}
                  />
                )}
              </div>
              <div className="rounded-xl border bg-muted/40 p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="tabular-nums">{money(remitoBase)}</span>
                </div>
                {remitoRecargo > 0 && (
                  <div className="flex justify-between text-sm text-amber-600 dark:text-amber-500">
                    <span>{esPagoMixto ? 'Recargo (sobre la parte con tarjeta)' : `Recargo ${recargoPct}%`}</span>
                    <span className="tabular-nums">+{money(remitoRecargo)}</span>
                  </div>
                )}
                <div className="flex items-baseline justify-between border-t pt-2">
                  <span className="text-sm font-medium">Total</span>
                  <span className="text-2xl font-bold tabular-nums">{money(remitoTotal)}</span>
                </div>
              </div>
              <div className="mt-auto hidden flex-col gap-2 md:flex">
                <Button size="lg" className="h-12 text-base" onClick={emitirRemitoPend} disabled={remitoEmitiendo}>
                  {remitoEmitiendo ? 'Emitiendo...' : 'Emitir remito'}
                </Button>
                <Button size="lg" variant="outline" className="h-11" onClick={() => setRemitoPend(null)} disabled={remitoEmitiendo}>
                  Después
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 md:hidden">
            <Button variant="outline" onClick={() => setRemitoPend(null)} disabled={remitoEmitiendo}>Después</Button>
            <Button onClick={emitirRemitoPend} disabled={remitoEmitiendo}>
              {remitoEmitiendo ? 'Emitiendo...' : 'Emitir remito'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!plateFor} onOpenChange={(o) => { if (!o) { setPlateFor(null); setPickedVehicle(null); setNewPlate(''); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Asignar vehículo</DialogTitle>
            <DialogDescription>
              {plateFor?.appointmentCode} — {fullName(plateFor?.client) || 'cliente'}. Elegí un vehículo ya cargado del cliente o escribí una patente nueva para crearlo.
            </DialogDescription>
          </DialogHeader>
          <VehicleCombobox
            vehicles={clientVehicles}
            selectedVehicle={pickedVehicle}
            onSelect={(v) => { setPickedVehicle(v); setNewPlate(''); }}
            onCreateVehicle={(q) => { setNewPlate(q.toUpperCase().replace(/[^A-Z0-9]/g, '')); setPickedVehicle(null); }}
            placeholder="Buscar patente del cliente o escribir una nueva..."
          />
          {newPlate && !pickedVehicle && (
            <p className="text-xs text-muted-foreground">Se creará el vehículo con la patente <span className="font-semibold text-foreground">{newPlate}</span> (se completan marca/modelo automáticamente).</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPlateFor(null); setPickedVehicle(null); setNewPlate(''); }}>Cancelar</Button>
            <Button onClick={submitPlate} disabled={plateSaving || (!pickedVehicle && !newPlate)}>
              {plateSaving ? 'Guardando...' : 'Asignar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Completar/corregir datos del cliente (o vincular un "Sin datos" a un cliente real) */}
      <ClientDataDialog
        open={!!clientDataFor}
        onOpenChange={(o) => { if (!o) setClientDataFor(null); }}
        clientId={clientDataFor}
        onSaved={onChange}
      />

      {/* Km del tablero al completar, para las OT que llegaron hasta acá sin km (opcional) */}
      <KmIngresoDialog
        open={!!kmCompletar}
        onOpenChange={(v) => { if (!v) setKmCompletar(null); }}
        titulo="Completar trabajo"
        vehiculo={kmCompletar ? [kmCompletar.vehicle?.licensePlate, kmCompletar.vehicle?.make, kmCompletar.vehicle?.model].filter(Boolean).join(' · ') : ''}
        confirmLabel="Cargar km y completar"
        skipLabel="Completar sin km"
        onSkip={async () => {
          const it = kmCompletar;
          if (!it) return;
          setKmCompletar(null);
          await postCompletar(it, undefined, true);
        }}
        onConfirm={async (km) => {
          const it = kmCompletar;
          if (!it) return;
          setKmCompletar(null);
          await postCompletar(it, km);
        }}
      />
    </div>
  );
};

// ============================================================
// Columna por estación
// ============================================================

const StationColumn: React.FC<{
  station: Station;
  items: QueueItem[];
  serviceReports: Record<string, ServiceReport>;
  tick: number;
  selectedAppointmentId: string | null;
  onSelect: (id: string) => void;
  onChange: () => void;
}> = ({ station, items, serviceReports, tick, selectedAppointmentId, onSelect, onChange }) => {
  const inProcess = items.filter((it) => it.status === 'in_progress');
  const queued = items.filter((it) => it.status !== 'in_progress');

  return (
    <Card className="flex flex-col min-w-0">
      <CardHeader className="pb-3 px-3 pt-3">
        <div className="flex items-center justify-between gap-2 min-w-0">
          <CardTitle className="text-base flex items-center gap-1.5 min-w-0 flex-1">
            <span className="inline-block w-2 h-2 rounded-full bg-cyan-500 shrink-0" />
            <span className="truncate">{station.name}</span>
            <Badge variant="secondary" className="shrink-0 px-1.5 text-xs tabular-nums">{items.length}</Badge>
          </CardTitle>
          <MonitorUrlButton stationKey={station.key} stationName={station.name} />
        </div>
        {station.description && (
          <p className="text-xs text-muted-foreground line-clamp-2">{station.description}</p>
        )}
      </CardHeader>
      <CardContent className="flex-1 space-y-3">
        {/* En proceso ahora */}
        {inProcess.length > 0 && (
          <div>
            <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">En proceso</div>
            {inProcess.map((it) => (
              <VehicleCard
                key={it.id}
                item={it}
                report={serviceReports[it.id]}
                tick={tick}
                isHighlight
                isSelected={selectedAppointmentId === it.id}
                onSelect={() => onSelect(it.id)}
              />
            ))}
          </div>
        )}

        {/* En espera */}
        {queued.length > 0 && (
          <div>
            <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">
              En espera ({queued.length})
            </div>
            <div className="space-y-2">
              {queued.map((it) => (
                <VehicleCard
                  key={it.id}
                  item={it}
                  report={serviceReports[it.id]}
                  tick={tick}
                  isSelected={selectedAppointmentId === it.id}
                  onSelect={() => onSelect(it.id)}
                />
              ))}
            </div>
          </div>
        )}

        {items.length === 0 && (
          <div className="text-center py-8 text-sm text-muted-foreground">
            Sin vehículos en {station.name.toLowerCase()}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================================
// Card individual de vehículo
// ============================================================

const VehicleCard: React.FC<{
  item: QueueItem;
  report?: ServiceReport;
  tick: number;
  isHighlight?: boolean;
  isSelected?: boolean;
  onSelect: () => void;
}> = ({ item, report, tick, isHighlight, isSelected, onSelect }) => {
  const elapsed = item.status === 'in_progress' && report?.startedAt
    ? elapsedSeconds(report.startedAt, tick)
    : item.checkedInAt ? Math.floor((tick - new Date(item.checkedInAt).getTime()) / 1000) : 0;

  const totalItems = (report?.products?.length || 0) + (report?.services?.length || 0);
  const plate = item.vehicle?.licensePlate || report?.licensePlate || '';
  const vMake = item.vehicle?.make || report?.make || '';
  const vModel = item.vehicle?.model || report?.model || '';
  const vehDesc = [vMake, vModel].filter(Boolean).join(' ');
  const cName = fullName(item.client) || fullName(report) || '-';
  const cDisplay = cName === 'Sin datos' && plate ? `Sin datos · ${plate}` : cName;

  return (
    <div
      onClick={onSelect}
      className={`cursor-pointer rounded-lg px-3 py-2.5 transition-all border flex flex-col gap-2 ${
        isHighlight ? 'border-cyan-500 bg-card' : 'border-border hover:border-cyan-300'
      } ${isSelected ? 'ring-2 ring-cyan-500' : ''}`}
    >
      {/* Fila principal: info del vehiculo + tiempo + chevron */}
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-mono text-sm font-bold text-cyan-600 dark:text-cyan-400 shrink-0">{item.appointmentCode || 'OT-?'}</span>
            <span className="text-base font-semibold truncate">{plate || '—'}</span>
            {item.isReclamo && (
              <Tip label={`Reclamo${item.reclamoType ? ': ' + item.reclamoType.replace(/_/g,' ') : ''}`}><span className="inline-flex items-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white shrink-0">
                🚨
              </span></Tip>
            )}
          </div>
          <div className="text-sm text-muted-foreground truncate mt-0.5">
            {cDisplay}
            {vehDesc && <span className="ml-1 opacity-70">· {vehDesc}</span>}
          </div>
        </div>

        <div className="shrink-0 text-right text-sm text-muted-foreground space-y-0.5">
          <div className="flex items-center gap-1 justify-end tabular-nums">
            <ClockIcon className="h-3.5 w-3.5" />
            {formatTime(elapsed)}
          </div>
          {item.status === 'in_progress' && (
            <div className="text-emerald-600 dark:text-emerald-400 font-medium text-xs">● en proceso</div>
          )}
          {totalItems > 0 && item.status !== 'in_progress' && (
            <div className="text-xs">{totalItems} tarea{totalItems > 1 ? 's' : ''}</div>
          )}
        </div>

        <ChevronRightIcon className="h-4 w-4 text-muted-foreground shrink-0" />
      </div>

      {/* Fila secundaria: recorrido por estaciones (abajo, ancho completo) */}
      {item.stationQueue && item.stationQueue.length > 1 && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground flex-wrap border-t border-border/50 pt-1.5">
          {item.stationQueue.map((sk, i) => (
            <React.Fragment key={sk}>
              {i > 0 && <ArrowRightIcon className="h-3 w-3 shrink-0" />}
              <span className={item.stationsCompleted?.includes(sk) ? 'line-through opacity-60' : (sk === item.currentStation ? 'font-semibold text-cyan-600' : '')}>
                {sk}
              </span>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================
// Panel lateral con detalles del vehículo
// ============================================================

const VehicleDetailPanel: React.FC<{
  queueItem: QueueItem;
  report: ServiceReport | null;
  stations: Station[];
  tick: number;
  onClose: () => void;
  onChange: () => void;
  modalOnly?: boolean;
  initialTab?: string;
}> = ({ queueItem, report, stations, tick, onClose, onChange, modalOnly = false, initialTab = 'tareas' }) => {
  const [tab, setTab] = useState(initialTab);
  useEffect(() => { setTab(initialTab); }, [initialTab]);
  const [expanded, setExpanded] = useState(false);
  const asModal = modalOnly || expanded;
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  // Se quiso completar una OT con vehículo que sigue sin km: se pide acá (21/09/2026)
  const [kmCompletar, setKmCompletar] = useState(false);

  const canStart = queueItem.status === 'checked_in';
  const canPause = queueItem.status === 'in_progress';
  const inWorkflow = ['in_progress', 'checked_in'].includes(queueItem.status);
  // ¿Hay una próxima estación pendiente (distinta a la actual)?
  const hasNextStation = inWorkflow && (queueItem.stationQueue || []).some(
    (k) => k !== queueItem.currentStation && !(queueItem.stationsCompleted || []).includes(k)
  );
  const canAdvance = inWorkflow && hasNextStation;
  const canCompleteWork = inWorkflow && !hasNextStation;

  const elapsed = queueItem.status === 'in_progress'
    ? (report?.startedAt
        ? elapsedSeconds(report.startedAt, tick)
        : queueItem.checkedInAt
          ? Math.floor((tick - new Date(queueItem.checkedInAt).getTime()) / 1000)
          : 0)
    : 0;

  const handleStart = async () => {
    setActionLoading('start');
    try {
      // Cambiar status del appointment a in_progress (el service-report se crea o actualiza)
      await axios.patch(`/api/appointments-enhanced/${queueItem.id}`, { status: 'in_progress' });
      toast.success('Servicio iniciado');
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al iniciar');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePause = async () => {
    if (!report) return;
    setActionLoading('pause');
    try {
      await axios.put(`/api/service-reports/${report.id}`, { status: 'paused' });
      toast.success('Servicio pausado');
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al pausar');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAdvance = async () => {
    setActionLoading('advance');
    try {
      const resp = await axios.post(`/api/reception/${queueItem.id}/advance-station`);
      const next = stations.find((s) => s.key === resp.data.nextStation);
      toast.success(`Avanzado a ${next?.name || resp.data.nextStation}`);
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al avanzar');
    } finally {
      setActionLoading(null);
    }
  };

  // Si la OT tiene vehículo y sigue sin km, el server avisa una vez (KM_MISSING):
  // se abre el diálogo para cargarlo o completar sin km (es opcional, 22/09/2026).
  const postCompletar = async (km?: number, sinKm = false) => {
    setActionLoading('complete');
    try {
      await axios.post(`/api/reception/${queueItem.id}/complete-work`, km != null ? { mileage: km } : sinKm ? { sinKm: true } : {});
      toast.success('Trabajo terminado');
      onClose();
      onChange();
    } catch (err: any) {
      if (err.response?.data?.code === 'KM_MISSING') { setKmCompletar(true); return; }
      toast.error(err.response?.data?.error || 'Error al completar');
    } finally {
      setActionLoading(null);
    }
  };

  const handleComplete = async () => {
    if (!(await confirmDialog({
      title: 'Completar trabajo',
      description: 'El vehículo pasará a "Listo para retirar". Después podés avisar al cliente con el botón "Avisar al cliente".',
      confirmText: 'Completar',
    }))) return;
    await postCompletar();
  };

  const handleWhatsAppMessage = () => {
    const phone = (queueItem.client?.phone || report?.phone || '').replace(/\D/g, '');
    if (!phone) {
      toast.error('Sin teléfono del cliente');
      return;
    }
    window.open(`https://wa.me/${phone}`, '_blank');
  };

  const currentStation = stations.find((s) => s.key === queueItem.currentStation);

  const body = (
    <Card
      onDoubleClick={(e) => { if (!asModal) { const t = e.target as HTMLElement; if (!t.closest('button,a,input,textarea,label')) setExpanded(true); } }}
      className={asModal
      ? "flex flex-col w-full max-w-4xl my-4 max-h-[92vh] overflow-y-auto shadow-2xl"
      : "flex flex-col h-fit max-h-[calc(100vh-180px)] sticky top-4"}>
      <CardHeader className="pb-3 border-b">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm font-semibold text-cyan-700 dark:text-cyan-400">
                {queueItem.appointmentCode}
              </span>
              {currentStation && (
                <Badge variant="outline" className="text-xs">
                  {currentStation.name}
                </Badge>
              )}
              {queueItem.isReclamo && (
                <Badge className="bg-red-500 text-white text-xs hover:bg-red-500">
                  🚨 Reclamo
                </Badge>
              )}
            </div>
            {queueItem.isReclamo && queueItem.reclamoType && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
                {queueItem.reclamoType.replace(/_/g, ' ')}
                {queueItem.reclamoNotes && ` · ${queueItem.reclamoNotes}`}
              </p>
            )}
            <CardTitle className="text-base mt-1">
              {(() => {
                const plate = queueItem.vehicle?.licensePlate || report?.licensePlate || '';
                const make = queueItem.vehicle?.make || report?.make || '';
                const model = queueItem.vehicle?.model || report?.model || '';
                const desc = [make, model].filter(Boolean).join(' ');
                return [plate || '-', desc].filter(Boolean).join(' · ');
              })()}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {(() => {
                const plate = queueItem.vehicle?.licensePlate || report?.licensePlate || '';
                const cName = fullName(queueItem.client) || fullName(report) || '';
                return cName === 'Sin datos' && plate ? `Sin datos · ${plate}` : cName;
              })()}
            </p>
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            {!modalOnly && (
              <Tip label={expanded ? 'Contraer' : 'Expandir'}><Button variant="ghost" size="icon" onClick={() => setExpanded((v) => !v)} className="size-8">
                {expanded ? <ArrowsPointingInIcon className="h-4 w-4" /> : <ArrowsPointingOutIcon className="h-4 w-4" />}
              </Button></Tip>
            )}
            <Button variant="ghost" size="icon" onClick={() => { setExpanded(false); onClose(); }} className="size-8">
              <XMarkIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Cronómetro grande */}
        {queueItem.status === 'in_progress' && (
          <div className="flex items-center justify-center bg-emerald-50 dark:bg-emerald-900/20 rounded-lg py-2 mt-2">
            <ClockIcon className="h-5 w-5 mr-2 text-emerald-600" />
            <span className="font-mono text-2xl tabular-nums font-semibold text-emerald-700 dark:text-emerald-400">
              {formatTime(elapsed)}
            </span>
          </div>
        )}

        {/* Acciones principales */}
        <div className="flex flex-wrap gap-2 mt-3">
          {canStart && (
            <Button
              size="sm"
              onClick={handleStart}
              disabled={!!actionLoading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <PlayIcon className="h-4 w-4 mr-1" />
              Iniciar
            </Button>
          )}
          {canPause && (
            <Button
              size="sm"
              variant="outline"
              onClick={handlePause}
              disabled={!!actionLoading}
            >
              <PauseIcon className="h-4 w-4 mr-1" />
              Pausar
            </Button>
          )}
          {canAdvance && (
            <Tip label="Avanzar a la próxima estación"><Button
              size="sm"
              variant="outline"
              onClick={handleAdvance}
              disabled={!!actionLoading}
            >
              <ArrowRightIcon className="h-4 w-4 mr-1" />
              Avanzar
            </Button></Tip>
          )}
          {canCompleteWork && (
            <Tip label="Marcar el trabajo como terminado (el aviso al cliente se envía después manualmente)"><Button
              size="sm"
              onClick={handleComplete}
              disabled={!!actionLoading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <CheckIcon className="h-4 w-4 mr-1" />
              Completar
            </Button></Tip>
          )}
          <Tip label="Hablar al cliente por WhatsApp"><Button
            size="sm"
            variant="outline"
            onClick={handleWhatsAppMessage}
            className="bg-green-50 text-green-700 hover:bg-green-100 border-green-200"
          >
            <WhatsAppIcon className="h-4 w-4" />
          </Button></Tip>
        </div>
      </CardHeader>

      <CardContent className="flex-1 overflow-y-auto p-0">
        {/* Lo que este auto debe, arriba de todo: es el momento en que se vende,
            con el vehículo y el cliente presentes. */}
        {queueItem.vehicle?.id && (
          <div className="px-4 pt-4">
            <VehicleServicePanel vehicleId={queueItem.vehicle.id} compacto />
          </div>
        )}
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 rounded-none">
            <TabsTrigger value="tareas">
              <WrenchScrewdriverIcon className="h-4 w-4 mr-1" />
              Items
            </TabsTrigger>
            <TabsTrigger value="fotos">
              <PhotoIcon className="h-4 w-4 mr-1" />
              Fotos
              {(report?.photos?.length || 0) > 0 && (
                <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">
                  {report?.photos?.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="notas">
              <ChatBubbleLeftIcon className="h-4 w-4 mr-1" />
              Notas
            </TabsTrigger>
          </TabsList>

          <TabsContent value="tareas" className="p-4">
            <TareasTab report={report} appointmentId={queueItem.id} queueItem={queueItem} onChange={onChange} />
          </TabsContent>

          <TabsContent value="fotos" className="p-4">
            <FotosTab report={report} appointmentId={queueItem.id} onChange={onChange} />
          </TabsContent>
          <TabsContent value="notas" className="p-4">
            <NotasTab report={report} onChange={onChange} />
          </TabsContent>
        </Tabs>
      </CardContent>

    </Card>
  );

  // Mismo Dialog compartido que usa el "Completar trabajo" (confirmDialog) de este panel
  const dialogoKm = (
    <KmIngresoDialog
      open={kmCompletar}
      onOpenChange={setKmCompletar}
      titulo="Completar trabajo"
      vehiculo={[queueItem.vehicle?.licensePlate, queueItem.vehicle?.make, queueItem.vehicle?.model].filter(Boolean).join(' · ')}
      confirmLabel="Cargar km y completar"
      skipLabel="Completar sin km"
      onSkip={async () => {
        setKmCompletar(false);
        await postCompletar(undefined, true);
      }}
      onConfirm={async (km) => {
        setKmCompletar(false);
        await postCompletar(km);
      }}
    />
  );

  if (asModal) {
    return (
      <>
        {createPortal(
          <div className="fixed inset-0 z-[9998] bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
            onClick={(e) => { if (e.target === e.currentTarget) { if (modalOnly) onClose(); else setExpanded(false); } }}>
            {body}
          </div>,
          document.body
        )}
        {dialogoKm}
      </>
    );
  }
  return <>{body}{dialogoKm}</>;
};

// ============================================================
// Tab: Tareas (items)
// ============================================================

const TareasTab: React.FC<{ report: ServiceReport | null; appointmentId: string; queueItem: QueueItem; onChange: () => void }> = ({ report, appointmentId, queueItem, onChange }) => {
  // Operarios: ven la lista de items (su trabajo) pero no editan la OT
  const { user: tareasUser } = useAuth();
  const isOperario = tareasUser?.role === 'operario';
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [adding, setAdding] = useState(false);
  const [savingQty, setSavingQty] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);

  useEffect(() => {
    axios.get('/api/products?limit=all').then((res) => {
      setProducts(res.data.products || res.data || []);
    }).catch(() => setProducts([]));
  }, []);

  const filteredOptions = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return [];
    return products
      .filter((p) => p.name.toLowerCase().includes(q))
      .slice(0, 30);
  }, [products, searchTerm]);

  const allItems = useMemo(() => {
    const out: Array<{ name: string; quantity: number; type: 'product' | 'service'; id?: string; source: 'report' | 'appointment'; idx: number }> = [];
    const reportHasItems = !!report && (((report.products || []).length + (report.services || []).length) > 0);
    if (reportHasItems && report) {
      // Si el SR tiene items, son la fuente de verdad
      (report.services || []).forEach((s, i) => out.push({ name: s.name, quantity: s.quantity, type: 'service', id: s.id, source: 'report', idx: i }));
      (report.products || []).forEach((p, i) => out.push({ name: p.name, quantity: p.quantity, type: 'product', id: p.id, source: 'report', idx: i }));
    } else {
      // Si el SR está vacío o no existe, los items viven en el turno (appointment)
      (queueItem.services || []).forEach((s, i) => out.push({ name: s.name, quantity: s.quantity ?? 1, type: 'service', id: s.id, source: 'appointment', idx: i }));
      (queueItem.products || []).forEach((p, i) => out.push({ name: p.name, quantity: p.quantity ?? 1, type: 'product', id: p.id, source: 'appointment', idx: i }));
    }
    return out;
  }, [report, queueItem]);

  // El remito en borrador de la OT se rearma solo al tocar los ítems (v12.4.5);
  // si ya está emitido el cambio no le llega y se avisa.
  const avisarRemito = (data: any) => {
    const r = data?.remito;
    if (r?.estado === 'actualizado') toast.success(`Remito ${r.remitNumber} actualizado`);
    else if (r?.estado === 'emitido') toast.warning(`El remito ${r.remitNumber} ya está emitido: este cambio no se pasó al remito`);
  };

  const addItem = async (option: ProductOption) => {
    setAdding(true);
    try {
      const isService = option.category === 'service' || option.type === 'service'
        || option.category === 'servicio' || option.type === 'servicio';
      const newItem = {
        id: option.id,
        name: option.name,
        quantity: 1,
        unitPrice: option.salePrice || 0,
        totalPrice: option.salePrice || 0,
        type: isService ? 'service' : 'product'
      };

      let currentReport = report;

      // Si no hay SR, lo creamos (o traemos el existente) de forma confiable y síncrona.
      if (!currentReport) {
        const ensureRes = await axios.post(`/api/service-reports/ensure/${appointmentId}`);
        currentReport = ensureRes.data.report;
        // Si recién se creó, sembrarlo con los ítems que estaban en el turno para no perderlos
        // (al haber SR, éste pasa a ser la fuente de verdad de la lista).
        if (ensureRes.data.created && currentReport) {
          const seedServices = (queueItem.services || []).map((s: any) => ({ ...s, quantity: s.quantity ?? 1 }));
          const seedProducts = (queueItem.products || []).map((p: any) => ({ ...p, quantity: p.quantity ?? 1 }));
          if (seedServices.length || seedProducts.length) {
            currentReport = { ...currentReport, services: seedServices, products: seedProducts };
          }
        }
      }
      if (!currentReport) { toast.error('No se pudo preparar el reporte de la OT'); return; }

      const updatedProducts = isService ? (currentReport.products || []) : [...(currentReport.products || []), newItem];
      const updatedServices = isService ? [...(currentReport.services || []), newItem] : (currentReport.services || []);
      const res = await axios.put(`/api/service-reports/${currentReport.id}`, {
        products: updatedProducts,
        services: updatedServices
      });
      avisarRemito(res.data);
      setSearchTerm('');
      setShowSuggestions(false);
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al agregar item');
    } finally {
      setAdding(false);
    }
  };

  const updateItemQuantity = async (
    item: { name: string; type: 'product' | 'service'; source: 'report' | 'appointment'; idx: number },
    newQty: number
  ) => {
    const qty = Math.max(1, Math.floor(Number(newQty) || 1));
    setSavingQty(true);
    try {
      const applyTo = (arr: any[]) => {
        if (arr[item.idx]) {
          const up = Number(arr[item.idx].unitPrice ?? arr[item.idx].price ?? 0);
          arr[item.idx] = { ...arr[item.idx], quantity: qty, totalPrice: up * qty };
        }
      };
      if (item.source === 'report') {
        if (!report) return;
        const services = [...(report.services || [])];
        const products = [...(report.products || [])];
        applyTo(item.type === 'service' ? services : products);
        avisarRemito((await axios.put(`/api/service-reports/${report.id}`, { products, services })).data);
      } else {
        const services = [...(queueItem.services || [])];
        const products = [...(queueItem.products || [])];
        applyTo(item.type === 'service' ? services : products);
        avisarRemito((await axios.put(`/api/appointments-enhanced/${appointmentId}/items`, { services, products })).data);
      }
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al actualizar la cantidad');
    } finally {
      setSavingQty(false);
    }
  };

  const removeItem = async (item: { name: string; type: 'product' | 'service'; id?: string; source: 'report' | 'appointment'; idx: number }) => {
    try {
      if (item.source === 'report') {
        if (!report) return;
        const services = [...(report.services || [])];
        const products = [...(report.products || [])];
        if (item.type === 'service') services.splice(item.idx, 1); else products.splice(item.idx, 1);
        avisarRemito((await axios.put(`/api/service-reports/${report.id}`, { products, services })).data);
      } else {
        // Item del turno (appointment): borrar por índice exacto y persistir
        const services = [...(queueItem.services || [])];
        const products = [...(queueItem.products || [])];
        if (item.type === 'service') services.splice(item.idx, 1); else products.splice(item.idx, 1);
        avisarRemito((await axios.put(`/api/appointments-enhanced/${appointmentId}/items`, { services, products })).data);
      }
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al eliminar item');
    }
  };

  const [showPage, setShowPage] = useState(false);

  return (
    <div className="space-y-2">
      {/* Header con botón lápiz */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase text-muted-foreground">
          {allItems.length > 0 ? `${allItems.length} ítem${allItems.length > 1 ? 's' : ''}` : 'Sin ítems'}
        </span>
        {!isOperario && (
          <Tip label="Editar productos y servicios"><Button
            variant="ghost"
            size="icon"
            className="size-7 text-muted-foreground hover:text-foreground"
            onClick={() => setShowPage(true)}
          >
            <PencilSquareIcon className="h-4 w-4" />
          </Button></Tip>
        )}
      </div>

      {/* Lista inline de ítems */}
      {allItems.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          Sin productos o servicios.
          {!isOperario && <>{' '}<button className="underline hover:text-foreground" onClick={() => setShowPage(true)}>Agregar</button></>}
        </p>
      ) : (
        <div className="space-y-1">
          {allItems.map((item, idx) => (
            <div key={`${item.type}-${item.id || idx}`} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/40 text-sm">
              <span className={`inline-flex rounded px-1 py-0.5 text-[10px] font-medium shrink-0 ${item.type === 'service' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'}`}>
                {item.type === 'service' ? 'SVC' : 'PROD'}
              </span>
              <span className="flex-1 truncate">{item.name}</span>
              <span className="text-xs text-muted-foreground tabular-nums shrink-0">×{item.quantity}</span>
            </div>
          ))}
        </div>
      )}

      {/* Página completa: tabla OT + catálogo — portal al body para evitar clipping del Card */}
      {showPage && createPortal(
        <div className="fixed inset-0 z-[9999] bg-background flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <div>
              <h2 className="text-lg font-semibold">Productos y servicios de la OT</h2>
              {!report && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                  Sin service-report activo — se creará al agregar el primer ítem.
                </p>
              )}
            </div>
            <Button variant="ghost" size="icon" onClick={() => { setShowPage(false); setSearchTerm(''); }}>
              <XMarkIcon className="h-5 w-5" />
            </Button>
          </div>

          <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-2 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-border">

            {/* Columna izquierda: ítems en la OT */}
            <div className="overflow-y-auto p-6 flex flex-col gap-3">
              <h3 className="text-sm font-semibold uppercase text-muted-foreground">Ítems en la OT ({allItems.length})</h3>
              {allItems.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">Sin ítems cargados.</p>
              ) : (
                <table className="w-full text-sm border border-border rounded-md overflow-hidden">
                  <thead>
                    <tr className="bg-muted/50 text-xs uppercase text-muted-foreground">
                      <th className="text-left px-3 py-2">Descripción</th>
                      <th className="text-center px-3 py-2 w-16">Cant.</th>
                      <th className="text-center px-3 py-2 w-20">Tipo</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {allItems.map((item, idx) => (
                      <tr key={`${item.type}-${item.id || idx}`} className={`border-t border-border ${idx % 2 === 0 ? '' : 'bg-muted/20'}`}>
                        <td className="px-3 py-2.5 font-medium">{item.name}</td>
                        <td className="px-3 py-2.5 text-center">
                          <input
                            type="number"
                            min={1}
                            key={`qty-${item.type}-${item.id || idx}-${item.quantity}`}
                            defaultValue={item.quantity}
                            disabled={savingQty}
                            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                            onBlur={(e) => {
                              const v = Math.max(1, Math.floor(Number(e.target.value) || 1));
                              if (v !== Number(item.quantity)) updateItemQuantity(item, v);
                            }}
                            className="w-14 text-center rounded border border-input bg-background px-1 py-0.5 text-sm tabular-nums disabled:opacity-50"
                          />
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium ${item.type === 'service' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'}`}>
                            {item.type === 'service' ? 'Servicio' : 'Producto'}
                          </span>
                        </td>
                        <td className="px-2 py-2.5 text-right">
                          <Button variant="ghost" size="icon" className="size-7 text-muted-foreground hover:text-red-500" onClick={() => removeItem(item)}>
                            <TrashIcon className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Columna derecha: catálogo para agregar */}
            <div className="overflow-y-auto p-6 flex flex-col gap-3">
              <h3 className="text-sm font-semibold uppercase text-muted-foreground">Catálogo</h3>
              <Input
                type="text"
                placeholder="Buscar producto o servicio..."
                value={searchTerm}
                autoFocus
                onChange={(e) => { setSearchTerm(e.target.value); setHighlightIndex(0); }}
                disabled={adding}
              />
              <div className="flex-1 rounded-md border border-border overflow-hidden">
                {searchTerm.trim().length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-12">Escribí para buscar en el catálogo...</p>
                )}
                {searchTerm.trim().length > 0 && filteredOptions.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-12">Sin resultados para "{searchTerm}"</p>
                )}
                {filteredOptions.map((opt, idx) => {
                  const isSvc = opt.category === 'servicio' || opt.type === 'service' || opt.type === 'servicio';
                  const alreadyIn = allItems.some((it) => it.name === opt.name);
                  return (
                    <div key={opt.id} className={`flex items-center gap-3 px-3 py-2.5 border-b border-border last:border-0 ${idx === highlightIndex ? 'bg-accent' : ''}`}>
                      <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium shrink-0 ${isSvc ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'}`}>
                        {isSvc ? 'Servicio' : 'Producto'}
                      </span>
                      <span className="flex-1 text-sm font-medium">{opt.name}</span>
                      {alreadyIn ? (
                        <span className="text-xs text-emerald-600 dark:text-emerald-400">✓ En OT</span>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-3 text-xs gap-1"
                          disabled={adding}
                          onClick={() => addItem(opt)}
                        >
                          <PlusIcon className="h-3 w-3" /> Agregar
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// ============================================================
// Tab: Fotos (drag & drop multi)
// ============================================================

const FotosTab: React.FC<{ report: ServiceReport | null; appointmentId?: string; onChange: () => void }> = ({ report, appointmentId, onChange }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const finishedInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [lightbox, setLightbox] = useState<string | null>(null);

  const uploadFiles = async (files: FileList | File[], type: 'trabajo' | 'terminado' = 'trabajo') => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      // Si todavía no existe el service report de la OT, lo creamos al vuelo.
      let reportId = report?.id;
      if (!reportId) {
        if (!appointmentId) { toast.error('No se pudo identificar la OT'); return; }
        const ens = await axios.post(`/api/service-reports/ensure/${appointmentId}`);
        reportId = ens.data?.report?.id;
        if (!reportId) { toast.error('No se pudo preparar el reporte'); return; }
      }
      const arr = Array.from(files);
      for (const file of arr) {
        const formData = new FormData();
        formData.append('photo', file);
        formData.append('type', type);
        if (type === 'terminado') formData.append('category', 'redes');
        await axios.post(`/api/service-reports/${reportId}/photos`, formData);
      }
      toast.success(`${arr.length} foto${arr.length > 1 ? 's' : ''} subida${arr.length > 1 ? 's' : ''}`);
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al subir foto');
    } finally {
      setUploading(false);
    }
  };

  const sendAllByWhatsApp = async () => {
    if (!report) return;
    try {
      await axios.post(`/api/service-reports/${report.id}/send-photos`);
      toast.success('Fotos enviadas por WhatsApp');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al enviar fotos');
    }
  };

  const deletePhoto = async (photoId: string) => {
    if (!report) return;
    if (!(await confirmDialog({
      title: 'Eliminar foto',
      description: '¿Seguro que querés eliminar esta foto? Esta acción no se puede deshacer.',
      confirmText: 'Eliminar',
      destructive: true,
    }))) return;
    try {
      await axios.delete(`/api/service-reports/${report.id}/photos/${photoId}`);
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al eliminar foto');
    }
  };

  if (!report && !appointmentId) {
    return (
      <div className="text-sm text-muted-foreground py-4 text-center">
        El service-report aún no se creó.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragActive(false);
          if (e.dataTransfer.files) uploadFiles(e.dataTransfer.files);
        }}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
          dragActive ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20' : 'border-border hover:border-cyan-300'
        }`}
      >
        <CameraIcon className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          {uploading ? 'Subiendo...' : 'Arrastrá fotos acá o hacé click'}
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => { if (e.target.files) uploadFiles(e.target.files); }}
        />
      </div>

      {/* Foto terminada / redes */}
      <button
        type="button"
        onClick={() => finishedInputRef.current?.click()}
        disabled={uploading}
        className="w-full flex items-center justify-center gap-2 rounded-lg border border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700 text-sm py-2 hover:bg-fuchsia-100 transition-colors disabled:opacity-50 dark:bg-fuchsia-900/20 dark:border-fuchsia-800 dark:text-fuchsia-300"
      >
        <CameraIcon className="h-4 w-4" />
        Subir foto terminada (para redes)
      </button>
      <input
        ref={finishedInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => { if (e.target.files) uploadFiles(e.target.files, 'terminado'); }}
      />

      {/* Galería */}
      {(report?.photos?.length || 0) > 0 ? (
        <>
          <div className="grid grid-cols-3 gap-2">
            {report?.photos?.map((photo) => (
              <div key={photo.id} className="relative group rounded overflow-hidden bg-muted aspect-square cursor-pointer" onClick={() => setLightbox(photo.url)}>
                <img src={photo.url} alt={photo.description || 'foto'} className="w-full h-full object-cover" />
                {photo.type === 'terminado' && (
                  <span className="absolute bottom-1 left-1 bg-fuchsia-600 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded">redes</span>
                )}
                <button
                  onClick={() => deletePhoto(photo.id)}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
          <Button variant="outline" className="w-full bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100" onClick={sendAllByWhatsApp}>
            <WhatsAppIcon className="h-4 w-4 mr-2" />
            Enviar fotos al cliente por WhatsApp
          </Button>
        </>
      ) : (
        <div className="text-sm text-muted-foreground text-center py-4">Sin fotos cargadas</div>
      )}


      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="Foto ampliada" className="max-h-full max-w-full rounded-lg object-contain" />
          <button type="button" onClick={() => setLightbox(null)} className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================================
// Tab: Notas
// ============================================================

const NotasTab: React.FC<{ report: ServiceReport | null; onChange: () => void }> = ({ report, onChange }) => {
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);

  const notesArray = useMemo(() => {
    if (!report?.notes) return [];
    if (Array.isArray(report.notes)) return report.notes;
    if (typeof report.notes === 'string') {
      try {
        const parsed = JSON.parse(report.notes);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [{ note: report.notes, createdAt: '', userName: '' }];
      }
    }
    return [];
  }, [report]);

  const addNote = async () => {
    if (!report || !noteText.trim()) return;
    setSaving(true);
    try {
      await axios.post(`/api/service-reports/${report.id}/notes`, { note: noteText.trim() });
      setNoteText('');
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al guardar nota');
    } finally {
      setSaving(false);
    }
  };

  if (!report) {
    return (
      <div className="text-sm text-muted-foreground py-4 text-center">
        El service-report aún no se creó.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Input
          placeholder="Escribir nota rápida..."
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addNote(); } }}
          disabled={saving}
        />
        <Button onClick={addNote} disabled={saving || !noteText.trim()} size="icon">
          <PlusIcon className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-2">
        {notesArray.length === 0 && (
          <div className="text-sm text-muted-foreground text-center py-4">Sin notas todavía</div>
        )}
        {notesArray.slice().reverse().map((n: any, i: number) => (
          <div key={i} className="rounded border border-border p-2 text-sm">
            <div className="text-xs text-muted-foreground mb-0.5">
              {n.userName || 'Usuario'} · {n.createdAt ? new Date(n.createdAt).toLocaleString('es-AR') : ''}
            </div>
            <div>{n.note}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================
// Sección Terminados (listos para retirar)
// ============================================================

const ReadyForPickupSection: React.FC<{
  items: QueueItem[];
  serviceReports: Record<string, ServiceReport>;
  tick: number;
  onChange: () => void;
  notifyTemplate?: string; // ya no se usa - kept for compat
}> = ({ items, serviceReports, tick, onChange }) => {
  const { user, hasPermission } = useAuth();
  const canReopen = hasPermission('reopen_production');
  const isOperario = user?.role === 'operario';
  const [delivering, setDelivering] = useState<string | null>(null);
  const { pedirRetiro, dialogoRetiro } = useRetiro();
  const [notifying, setNotifying] = useState<string | null>(null);
  const REOPEN_REASONS = [
    'Cliente pidió trabajo adicional',
    'Se detectó un problema adicional',
    'Trabajo incompleto',
    'Error en el servicio realizado',
    'Otro',
  ];
  // Motivos donde tiene sentido agregar un servicio nuevo
  const REASONS_WITH_SERVICE = new Set(['Cliente pidió trabajo adicional', 'Se detectó un problema adicional', 'Otro']);
  const [reopenItem, setReopenItem] = useState<QueueItem | null>(null);
  // Informe de servicio de la OT lista para retirar (v12.3.0)
  const [informeOtId, setInformeOtId] = useState<string | null>(null);
  const [reopenReasonSelect, setReopenReasonSelect] = useState('');
  const [reopenReasonOther, setReopenReasonOther] = useState('');
  const reopenReason = reopenReasonSelect === 'Otro' ? reopenReasonOther : reopenReasonSelect;
  const [reopenNotify, setReopenNotify] = useState(false);
  // Lista de ítems a agregar (múltiples)
  const [reopenAddedItems, setReopenAddedItems] = useState<Array<{id: string; name: string; quantity: number; type?: string}>>([]);
  const [reopening, setReopening] = useState(false);
  const [serviceSearch, setServiceSearch] = useState('');
  const [serviceOptions, setServiceOptions] = useState<any[]>([]);
  const [serviceSearching, setServiceSearching] = useState(false);

  useEffect(() => {
    const q = serviceSearch.trim();
    if (q.length < 2) { setServiceOptions([]); return; }
    const timer = setTimeout(() => {
      setServiceSearching(true);
      axios.get(`/api/products?search=${encodeURIComponent(q)}&limit=20`)
        .then((res) => {
          setServiceOptions(res.data?.products || res.data || []);
        })
        .catch(() => { setServiceOptions([]); })
        .finally(() => setServiceSearching(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [serviceSearch]);

  const closeReopenModal = () => {
    setReopenItem(null);
    setReopenReasonSelect('');
    setReopenReasonOther('');
    setReopenNotify(false);
    setReopenAddedItems([]);
    setServiceSearch('');
    setServiceOptions([]);
  };

  const handleAddItemToList = (s: any) => {
    setReopenAddedItems((prev) => {
      const exists = prev.find((x) => x.id === s.id);
      if (exists) {
        return prev.map((x) => x.id === s.id ? { ...x, quantity: x.quantity + 1 } : x);
      }
      return [...prev, { id: s.id, name: s.name, quantity: 1, type: s.type }];
    });
    setServiceSearch('');
    setServiceOptions([]);
  };

  const handleReopen = async () => {
    if (!reopenItem || !reopenReason.trim()) return;
    setReopening(true);
    try {
      await axios.post(`/api/reception/${reopenItem.id}/reopen`, {
        reason: reopenReason.trim(),
        notifyClient: reopenNotify,
        newItems: reopenAddedItems,
        // compat: si solo hay 1 ítem
        newServiceId: reopenAddedItems[0]?.id || null,
        newServiceName: reopenAddedItems[0]?.name || null,
      });
      toast.success('OT reabierta para producción');
      closeReopenModal();
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al reabrir');
    } finally {
      setReopening(false);
    }
  };

  const handleNotify = async (item: QueueItem, alreadyNotified: boolean) => {
    if (!item.client?.phone) {
      toast.error('El cliente no tiene teléfono cargado');
      return;
    }
    if (alreadyNotified) {
      const name = item.client?.firstName || item.client?.companyName || 'cliente';
      if (!(await confirmDialog({
        title: 'Reenviar aviso',
        description: `Ya se le avisó a ${name}. ¿Volver a enviar el mensaje?`,
        confirmText: 'Reenviar',
      }))) return;
    }
    setNotifying(item.id);
    try {
      await axios.post(`/api/reception/${item.id}/notify-ready`);
      const name = item.client?.firstName || item.client?.companyName || 'cliente';
      toast.success(`Mensaje enviado a ${name}`);
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'No se pudo enviar el mensaje');
    } finally {
      setNotifying(null);
    }
  };

  const formatRelative = (iso?: string | null): string => {
    if (!iso) return '';
    const ms = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(ms) || ms < 0) return '';
    const s = Math.floor(ms / 1000);
    if (s < 60) return 'recién';
    const m = Math.floor(s / 60);
    if (m < 60) return `hace ${m} min`;
    const h = Math.floor(m / 60);
    if (h < 24) return `hace ${h} h`;
    return `hace ${Math.floor(h / 24)} d`;
  };

  const handleDeliver = async (item: QueueItem) => {
    const appointmentId = item.id;
    // Clientes que piden quién retira (Grumaq): el cartel con nombre y DNI ya es la confirmación
    const retiro = await pedirRetiro(appointmentId, subtituloRetiro(item));
    if (retiro === undefined) return;
    if (retiro === null && !(await confirmDialog({
      title: 'Confirmar retiro',
      description: '¿Confirmás que el cliente retiró el vehículo?',
      confirmText: 'Sí, retiró',
    }))) return;
    setDelivering(appointmentId);
    try {
      await axios.post(`/api/reception/${appointmentId}/deliver`, retiro ? { retiro } : {});
      toast.success('Vehículo entregado');
      onChange();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al marcar como entregado');
      // Rechazo por estado: la OT ya se entregó desde otra máquina y esta
      // tarjeta quedó vieja. Se vuelve a pedir la cola.
      if ([400, 404, 409].includes(Number(err?.response?.status))) onChange();
    } finally {
      setDelivering(null);
    }
  };

  return (
    <Card className="border-emerald-500/30">
      {dialogoRetiro}
      <CardHeader className="pb-2 pt-3 px-4">
        <div className="flex items-center gap-2">
          <CheckIcon className="h-4 w-4 text-emerald-600 shrink-0" />
          <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
            Listos para retirar
          </span>
          <Badge variant="secondary" className="ml-auto bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 text-xs">
            {items.length}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-3 space-y-1">
        {items.map((item) => {
          const report = serviceReports[item.id];
          const finishedAgo = item.checkedInAt
            ? Math.floor((tick - new Date(item.checkedInAt).getTime()) / 1000)
            : 0;
          const alreadyNotified = !!item.pickupNotifiedAt;
          const rawPlate = item.vehicle?.licensePlate || report?.licensePlate || '';
          const cName = fullName(item.client) || fullName(report) || '-';
          const clientName = cName === 'Sin datos' && rawPlate ? `Sin datos · ${rawPlate}` : cName;
          const plate = rawPlate || '-';
          const vehicle = [item.vehicle?.make || report?.make || '', item.vehicle?.model || report?.model || ''].filter(Boolean).join(' ') || '-';

          return (
            <div key={item.id} className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5 hover:bg-muted/30 transition-colors">
              {/* Info principal */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-sm font-bold text-cyan-600 dark:text-cyan-400 whitespace-nowrap">{item.appointmentCode || 'OT-?'}</span>
                  <span className="text-base font-semibold truncate">{plate}</span>
                  <span className="text-sm text-muted-foreground truncate">{vehicle}</span>
                </div>
                <div className="flex items-center gap-3 mt-0.5">
                  <span className="text-sm text-foreground truncate">{clientName}</span>
                  <span className="text-sm text-muted-foreground whitespace-nowrap flex items-center gap-0.5 ml-auto">
                    <ClockIcon className="h-3.5 w-3.5" /> {formatTime(finishedAgo)}
                  </span>
                </div>
              </div>

              {/* Remito como botón de texto */}
              {item.remitoNumber && (
                <Tip label={`Remito ${item.remitoNumber}`}><a
                  href="/remitos"
                  className="shrink-0 inline-flex items-center gap-1 text-sm font-medium text-cyan-600 dark:text-cyan-400 hover:underline border border-cyan-500/30 rounded px-2 py-1.5 whitespace-nowrap"
                >
                  <DocumentTextIcon className="h-3.5 w-3.5" />
                  {item.remitoNumber}
                </a></Tip>
              )}

              {/* Botones cuadrados con solo ícono */}
              <div className="flex items-center gap-1 shrink-0">
                {!isOperario && (
                  <Tip label="Informe de servicio (km, trabajos y revisión)"><button
                    type="button"
                    onClick={() => setInformeOtId(item.id)}
                    className="h-8 w-8 rounded flex items-center justify-center border border-border text-muted-foreground hover:bg-muted transition-colors"
                  >
                    <DocumentTextIcon className="h-4 w-4" />
                  </button></Tip>
                )}
                {!isOperario && item.client?.phone && (
                  <Tip label={alreadyNotified ? `Ya avisado ${formatRelative(item.pickupNotifiedAt)}. Click para reenviar.` : 'Avisar al cliente por WhatsApp'}><button
                    type="button"
                    onClick={() => handleNotify(item, alreadyNotified)}
                    disabled={notifying === item.id}
                    className={`h-8 w-8 rounded flex items-center justify-center border transition-colors ${
                      alreadyNotified
                        ? 'border-border text-muted-foreground hover:bg-muted'
                        : 'border-green-400/50 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
                    }`}
                  >
                    {alreadyNotified ? <CheckIcon className="h-4 w-4" /> : <WhatsAppIcon className="h-4 w-4" />}
                  </button></Tip>
                )}
                {!isOperario && (
                  <Tip label="Confirmar retiro del cliente"><button
                    type="button"
                    onClick={() => handleDeliver(item)}
                    disabled={delivering === item.id}
                    className="h-8 w-8 rounded flex items-center justify-center bg-emerald-600 hover:bg-emerald-700 text-white border border-emerald-600 transition-colors disabled:opacity-50"
                  >
                    {delivering === item.id
                      ? <div className="h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      : <CheckIcon className="h-4 w-4" />}
                  </button></Tip>
                )}
                {canReopen && (
                  <Tip label="Volver a producción"><button
                    type="button"
                    onClick={() => setReopenItem(item)}
                    className="h-8 w-8 rounded flex items-center justify-center border border-amber-500/50 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10 transition-colors"
                  >
                    <ArrowPathIcon className="h-4 w-4" />
                  </button></Tip>
                )}
              </div>
            </div>
          );
        })}
      </CardContent>

      <InformeServicioDialog
        appointmentId={informeOtId}
        open={!!informeOtId}
        onOpenChange={(o) => { if (!o) setInformeOtId(null); }}
      />

      {/* Modal: Volver a producción */}
      {reopenItem && (
        <Dialog open={!!reopenItem} onOpenChange={(o) => { if (!o) closeReopenModal(); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Volver a producción — {reopenItem.appointmentCode || 'OT'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">

              {/* Tabla: servicios/productos ya en la OT */}
              {(() => {
                const sr = serviceReports[reopenItem.id];
                const allItems = [
                  ...(sr?.services || []).map((s: any) => ({ ...s, _kind: 'Servicio' })),
                  ...(sr?.products || []).map((p: any) => ({
                    ...p,
                    _kind: (p.type === 'service' || p.type === 'servicio' || p.category === 'servicio') ? 'Servicio' : 'Producto',
                  })),
                ];
                if (allItems.length === 0) return null;
                return (
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium text-muted-foreground">Ítems actuales en la OT</Label>
                    <div className="rounded-md border border-border overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-muted/50 text-muted-foreground text-xs uppercase">
                            <th className="text-left px-3 py-2 font-medium">Descripción</th>
                            <th className="text-center px-3 py-2 font-medium w-16">Cant.</th>
                            <th className="text-right px-3 py-2 font-medium w-24">Tipo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allItems.map((it: any, idx: number) => (
                            <tr key={it.id || idx} className={idx % 2 === 0 ? '' : 'bg-muted/20'}>
                              <td className="px-3 py-2">{it.name}</td>
                              <td className="px-3 py-2 text-center tabular-nums">{it.quantity ?? 1}</td>
                              <td className="px-3 py-2 text-right">
                                <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ${it._kind === 'Servicio' ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400' : 'bg-amber-500/10 text-amber-600 dark:text-amber-400'}`}>{it._kind}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* Motivo */}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Motivo *</Label>
                <select
                  value={reopenReasonSelect}
                  onChange={(e) => setReopenReasonSelect(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">Seleccionar motivo...</option>
                  {REOPEN_REASONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
                {reopenReasonSelect === 'Otro' && (
                  <textarea
                    value={reopenReasonOther}
                    onChange={(e) => setReopenReasonOther(e.target.value)}
                    placeholder="Describí el motivo..."
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[70px] resize-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    autoFocus
                  />
                )}
              </div>

              {/* Agregar ítems (lista) — solo para motivos relevantes */}
              {REASONS_WITH_SERVICE.has(reopenReasonSelect) && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Agregar productos o servicios (opcional)</Label>

                  {/* Buscador */}
                  <div className="relative">
                    <input
                      type="text"
                      value={serviceSearch}
                      onChange={(e) => setServiceSearch(e.target.value)}
                      placeholder="Buscar y seleccionar para agregar a la lista..."
                      className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring pr-8"
                    />
                    {serviceSearching && (
                      <div className="absolute right-2 top-2.5">
                        <div className="h-4 w-4 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                    {serviceSearch.trim().length >= 2 && !serviceSearching && serviceOptions.length === 0 && (
                      <div className="absolute z-50 w-full mt-1 rounded-md border border-border bg-popover shadow-lg">
                        <p className="px-3 py-2 text-sm text-muted-foreground">Sin resultados</p>
                      </div>
                    )}
                    {serviceOptions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 rounded-md border border-border bg-popover shadow-lg max-h-[180px] overflow-y-auto">
                        {serviceOptions.map((s: any) => (
                          <button
                            key={s.id}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleAddItemToList(s)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-accent flex items-center gap-2"
                          >
                            <PlusIcon className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                            <span className="flex-1">{s.name}</span>
                            <span className="text-xs text-muted-foreground shrink-0">
                              {(s.type === 'service' || s.type === 'servicio' || s.category === 'servicio') ? 'Servicio' : 'Producto'}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Lista de ítems agregados */}
                  {reopenAddedItems.length > 0 && (
                    <div className="rounded-md border border-emerald-500/30 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-emerald-500/10 text-xs uppercase text-emerald-700 dark:text-emerald-400">
                            <th className="text-left px-3 py-2 font-medium">Ítem a agregar</th>
                            <th className="text-center px-3 py-2 font-medium w-28">Cantidad</th>
                            <th className="w-10"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {reopenAddedItems.map((it, idx) => (
                            <tr key={it.id} className={idx % 2 === 0 ? '' : 'bg-muted/20'}>
                              <td className="px-3 py-2 font-medium">{it.name}</td>
                              <td className="px-3 py-2 text-center">
                                <NumberInput
                                  min={1}
                                  value={it.quantity}
                                  onChange={(v) => {
                                    const q = v;
                                    setReopenAddedItems((prev) => prev.map((x) => x.id === it.id ? { ...x, quantity: q } : x));
                                  }}
                                  className="w-16 text-center rounded border border-input bg-background px-1 py-0.5 text-sm tabular-nums"
                                />
                              </td>
                              <td className="px-2 py-2 text-right">
                                <Tip label="Quitar"><button
                                  type="button"
                                  onClick={() => setReopenAddedItems((prev) => prev.filter((x) => x.id !== it.id))}
                                  className="text-muted-foreground hover:text-destructive transition-colors"
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button></Tip>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Notificar al cliente */}
              {reopenItem.client?.phone && reopenItem.pickupNotifiedAt && (
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={reopenNotify} onChange={(e) => setReopenNotify(e.target.checked)} />
                  <span>Avisar al cliente que se demoró un poco más</span>
                </label>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={closeReopenModal}>Cancelar</Button>
              <Button
                onClick={handleReopen}
                disabled={reopening || !reopenReason.trim()}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {reopening ? 'Procesando...' : 'Volver a producción'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
};

// ============================================================
// Botón "Abrir monitor" por estación (admin)
// ============================================================

const MonitorUrlButton: React.FC<{ stationKey: string; stationName: string }> = ({ stationKey, stationName }) => {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [urls, setUrls] = useState<{ operator: string; display: string } | null>(null);

  const handleClick = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/reception/stations/${stationKey}/monitor-url`);
      setUrls({
        operator: `${window.location.origin}/taller/monitor/${stationKey}?token=${res.data.monitorToken}&mode=operator`,
        display: `${window.location.origin}/taller/monitor/${stationKey}?token=${res.data.monitorToken}&mode=display`,
      });
      setOpen(true);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al obtener URL del monitor');
    } finally {
      setLoading(false);
    }
  };

  const openOperator = () => {
    if (!urls) return;
    window.open(urls.operator, '_blank');
    setOpen(false);
  };

  const openDisplay = () => {
    if (!urls) return;
    window.open(urls.display, '_blank');
    setOpen(false);
  };

  const copyUrls = async () => {
    if (!urls) return;
    const text = `OPERADOR (con PIN, interactivo):\n${urls.operator}\n\nDISPLAY (TV solo lectura):\n${urls.display}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('URLs copiadas al portapapeles');
      setOpen(false);
    } catch {
      window.prompt('Copiá las URLs:', text);
    }
  };

  return (
    <>
      <Tip label="Abrir monitor de esta estación"><Button
        variant="outline"
        size="icon"
        onClick={handleClick}
        disabled={loading}
        className="h-7 w-7 shrink-0"
      >
        <ComputerDesktopIcon className="h-3.5 w-3.5" />
      </Button></Tip>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ComputerDesktopIcon className="h-5 w-5" />
              Monitor — {stationName}
            </DialogTitle>
            <DialogDescription>
              Elegí cómo querés abrir el monitor de esta estación.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <button
              type="button"
              onClick={openOperator}
              className="w-full text-left rounded-lg border border-border bg-card hover:bg-accent transition-colors p-3"
            >
              <div className="font-medium text-sm">Modo Operador</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Con PIN, para interactuar desde la estación.
              </div>
            </button>

            <button
              type="button"
              onClick={openDisplay}
              className="w-full text-left rounded-lg border border-border bg-card hover:bg-accent transition-colors p-3"
            >
              <div className="font-medium text-sm">Modo Display</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Solo lectura, para TV o pantalla pública.
              </div>
            </button>
          </div>

          <DialogFooter className="sm:justify-between gap-2">
            <Button variant="outline" size="sm" onClick={copyUrls}>
              <ClipboardDocumentIcon className="h-4 w-4 mr-1.5" />
              Copiar URLs
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Workshop;
